import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
  useMemo,
} from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import "../../../App.css";
import Loader from "../Loader";
import Table from "../Table";
import Navbar from "../Navbar";
import Modal from "../Modal";
import {
  swalConfirm,
  swalError,
  swalSuccess,
  swalWarning,
  swalToast,
} from "../../../utils/swalAlert";
import AnnotationStudio from "./AnnotationStudio/AnnotationStudio";

import { fetchWithAuth } from "../../../utils/api";
import certificateService from "../../../services/certificateService";
import Report from "../../../components/pages/Instructor/pageUI/Results/Report";
import { useReactToPrint } from "react-to-print";
import PerformanceReport from "../PerformanceReport";
import { AuthContext } from "../../../context/AuthContext";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

const formatLatexText = (text) => {
  if (!text || typeof text !== "string") return text;
  let processed = text;

  // Pre-process common math functions to use LaTeX equivalents
  processed = processed.replace(
    /(^|[^\\])\b(sin|cos|tan|cot|sec|csc|log|ln)\b/gi,
    (match, p1, p2) => p1 + "\\" + p2.toLowerCase(),
  );
  processed = processed.replace(
    /(^|[^\\])sqrt\(([^)]+)\)/gi,
    (match, p1, p2) => p1 + "\\sqrt{" + p2 + "}",
  );

  if (
    !processed.includes("$") &&
    !processed.includes("\\(") &&
    !processed.includes("\\[")
  ) {
    const words = [];
    let current = "";
    let depth = 0;
    for (let i = 0; i < processed.length; i++) {
      const c = processed[i];
      if (c === "{" || c === "(") depth++;
      else if (c === "}" || c === ")") depth = Math.max(0, depth - 1);
      if (c === " " && depth === 0) {
        if (current) words.push(current);
        current = "";
      } else {
        current += c;
      }
    }
    if (current) words.push(current);

    const mathTokens = words.map((word) => {
      const hasMathSymbols = /(\\[a-zA-Z]+|\^|_)/.test(word);
      const isMathChars = /^[\d+*/()=.,<>!-]+$/.test(word);
      const isPurePunctuation = /^[.,]+$/.test(word);
      const isSingleVariable =
        /^[a-zA-Z][.,]?$/.test(word) &&
        !["a", "A", "I", "a.", "a,", "A.", "A,", "I.", "I,"].includes(word);
      return (
        hasMathSymbols ||
        isSingleVariable ||
        (isMathChars && !isPurePunctuation)
      );
    });

    let merged = [];
    let currentMath = [];
    for (let i = 0; i < words.length; i++) {
      if (mathTokens[i]) {
        currentMath.push(words[i]);
      } else {
        if (currentMath.length > 0) {
          merged.push(`$${currentMath.join(" ")}$`);
          currentMath = [];
        }
        merged.push(words[i]);
      }
    }
    if (currentMath.length > 0) {
      merged.push(`$${currentMath.join(" ")}$`);
    }
    processed = merged.join(" ");
  }

  processed = processed.replace(
    /(^|[^\\])\$([^$]+)\$/g,
    (match, before, math) => {
      if (math.includes("\\displaystyle")) return match;
      return `${before}$\\displaystyle ${math}$`;
    },
  );
  processed = processed.replace(/\\\((.*?)\\\)/g, (match, math) => {
    if (math.includes("\\displaystyle")) return match;
    return `\\(\\displaystyle ${math}\\)`;
  });
  return processed;
};

const SPAN_COMPONENTS = { p: "span" };
const REMARK_PLUGINS = [remarkMath, remarkGfm, remarkBreaks];
const REHYPE_PLUGINS = [
  [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
];

const LazyMarkdown = React.memo(({ children, components }) => (
  <ReactMarkdown
    children={children}
    remarkPlugins={REMARK_PLUGINS}
    rehypePlugins={REHYPE_PLUGINS}
    components={components}
  />
));

const getMCQOptionText = (question, answer) => {
  if (!answer || !Array.isArray(question?.options)) return answer || "-";

  const keys = (Array.isArray(answer) ? answer : String(answer).split(",")).map(
    (k) => k.trim(),
  );

  const mapped = keys.map((key) => {
    const matchedOpt = question.options.find(
      (opt) =>
        opt?.key &&
        String(opt.key).trim().toUpperCase() ===
          String(key).trim().toUpperCase(),
    );
    return matchedOpt ? `${matchedOpt.key}. ${matchedOpt.text}` : key;
  });

  return mapped.join(", ");
};

export default function Results({ role }) {
  const serverUrl = process.env.REACT_APP_SERVER_URL;
  const { user } = useContext(AuthContext);
  const [scoreData, setScoreData] = useState(null);
  const scoreDataRef = useRef(scoreData);
  useEffect(() => {
    scoreDataRef.current = scoreData;
  }, [scoreData]);
  const [classId, setClassId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("table");
  const [activeSubject, setActiveSubject] = useState("");
  const [activeSection, setActiveSection] = useState("");

  const [currentOEIndex, setCurrentOEIndex] = useState(0);
  const [isMobileScoreNavExpanded, setIsMobileScoreNavExpanded] =
    useState(false);
  const [isSolutionModalOpen, setIsSolutionModalOpen] = useState(false);
  const [solutionQuestion, setSolutionQuestion] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);

  // Instructor/Admin states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEditingQuestion, setCurrentEditingQuestion] = useState(null);
  const [editForm, setEditForm] = useState({
    questionNumber: "",
    studentAnswer: "",
    obtainedScore: "",
    maxScore: "",
    feedback: "",
    isManualReview: false,
    scoreChangeReason: "",
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [manualReview, setManualReview] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const hasShownReviewWarning = useRef(false);

  // Student states
  const initialRegradeRequestState = {
    questionId: null,
    regradeComment: "",
  };
  const [currentRegradeRequest, setCurrentRegradeRequest] = useState(
    initialRegradeRequestState,
  );
  const [isRegradeCommentModalOpen, setIsRegradeCommentModalOpen] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingRegradeRequests, setPendingRegradeRequests] = useState([]);
  const [isRegradeRequestSubmitted, setIsRegradeRequestSubmitted] =
    useState(false);
  const [submittedRegradeRequests, setSubmittedRegradeRequests] = useState({});
  const [isViewingRegradeRequests, setIsViewingRegradeRequests] =
    useState(false);

  // Common states
  const [answerPaperComments, setAnswerPaperComments] = useState("");
  const [answerPaperStatus, setAnswerPaperStatus] = useState("");
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [isSummaryGenerating, setIsSummaryGenerating] = useState(false);
  const [isSummaryEditing, setIsSummaryEditing] = useState(false);
  const [onSaveSummary, setOnSaveSummary] = useState(false);
  const [isSummarySaving, setIsSummarySaving] = useState(false);
  const summaryRef = useRef(null);
  const pathSegments = window.location.pathname.split("/");
  const answerPaperId = pathSegments[pathSegments.length - 1];

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoData, setInfoData] = useState({
    source: "",
    reasoning: "",
    isOnlineExam: false,
    timeTaken: null,
    avgCorrectTimeTaken: null,
  });
  const [proctoringLog, setProctoringLog] = useState(null);
  const [showProctoringModal, setShowProctoringModal] = useState(false);
  const [proctoringTab, setProctoringTab] = useState("violations"); // "violations" or "snapshots"
  const [enlargedSnapshotIndex, setEnlargedSnapshotIndex] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (enlargedSnapshotIndex === null || !proctoringLog?.snapshots) return;
      if (e.key === "ArrowLeft") {
        setEnlargedSnapshotIndex((prev) => Math.max(0, prev - 1));
      } else if (e.key === "ArrowRight") {
        setEnlargedSnapshotIndex((prev) =>
          Math.min(proctoringLog.snapshots.length - 1, prev + 1),
        );
      } else if (e.key === "Escape") {
        setEnlargedSnapshotIndex(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enlargedSnapshotIndex, proctoringLog]);

  const [classInfo, setClassInfo] = useState({
    id: null,
    name: "Loading...",
    instructorName: "",
  });
  const [testInfo, setTestInfo] = useState({
    id: null,
    name: "Loading...",
    isPracticeTest: false,
    annotationPrintingOffsetConfigId: null,
  });
  const [studentInfo, setStudentInfo] = useState({
    id: null,
    referenceId: "",
    name: "Unknown Student",
  });

  const [issuedCertificate, setIssuedCertificate] = useState(null);
  const [isDownloadingCert, setIsDownloadingCert] = useState(false);

  const [answerPaperURL, setAnswerPaperURL] = useState(null);
  const [testPaperURL, setTestPaperURL] = useState(null);

  // Annotations Dropdown & Viewer States
  const [showAnnotationsMenu, setShowAnnotationsMenu] = useState(false);
  const [isViewingAnnotatedPdf, setIsViewingAnnotatedPdf] = useState(false);
  const annotationsDropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        annotationsDropdownRef.current &&
        !annotationsDropdownRef.current.contains(e.target)
      ) {
        setShowAnnotationsMenu(false);
      }
    };
    if (showAnnotationsMenu) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showAnnotationsMenu]);

  const handleViewAnnotatedPdf = async () => {
    if (isViewingAnnotatedPdf) return;
    setShowAnnotationsMenu(false);

    const studentName =
      studentInfo?.name || studentInfo?.referenceId || "Student";

    const choice = await swalConfirm(
      "Select Annotation Format",
      "Choose whether to generate annotations on the student's submitted paper or on a blank paper.",
      "Annotation on Student Paper",
      "Annotation on Blank Paper",
      true,
    );

    if (!choice) return;

    let blankPaper = false;
    if (choice.isConfirmed) {
      blankPaper = false;
    } else if (choice.dismiss === "cancel" || choice.dismiss === "esc") {
      blankPaper = true;
    } else {
      return;
    }

    try {
      setIsViewingAnnotatedPdf(true);
      swalToast(
        "info",
        blankPaper
          ? "Preparing blank paper annotations PDF..."
          : "Preparing annotations PDF...",
        3000,
      );

      const offsetConfigId = testInfo?.annotationPrintingOffsetConfigId;
      const offsetParam =
        blankPaper && offsetConfigId
          ? `&annotationPrintingOffsetConfigId=${offsetConfigId}`
          : "";
      const query = blankPaper ? `?blankPaper=true${offsetParam}` : "";

      const response = await fetchWithAuth(
        `${serverUrl}/api/score/generate-annotation-pdf/${answerPaperId}${query}`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (!response || !response.ok) {
        throw new Error("Failed to fetch PDF");
      }

      const blob = await response.blob();
      const safeStudentName = studentName
        ? studentName.replace(/[^a-zA-Z0-9 ]/g, "_").trim()
        : "Student";
      const filename = blankPaper
        ? `Annotation_Blank_Paper_${safeStudentName}.pdf`
        : `Annotation_Student_Paper_${safeStudentName}.pdf`;

      const pdfUrl = URL.createObjectURL(blob);

      // Detect mobile devices to provide a fallback since iframes with PDFs fail on mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (isMobile) {
        // On mobile, trigger direct download and skip opening a new window
        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(pdfUrl);
        return;
      }

      const pdfWindow = window.open("", "_blank");

      if (pdfWindow) {
        pdfWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>${filename}</title>
            <meta name="viewport" content="width=device-width, initial-scale=1">
            <style>
              body { margin: 0; padding: 0; background-color: #525659; display: flex; flex-direction: column; height: 100vh; font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; overflow: hidden; }
              .header { background-color: #323639; color: #f1f1f1; padding: 12px 24px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 2px 4px rgba(0,0,0,0.2); z-index: 10; height: 32px; flex-shrink: 0; }
              .title { font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 60%; font-weight: 500; }
              .download-btn { background-color: #1a73e8; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 13px; font-weight: 600; transition: background-color 0.2s; display: flex; align-items: center; gap: 8px; }
              .download-btn:hover { background-color: #1557b0; }
              .download-btn svg { width: 16px; height: 16px; fill: currentColor; }
              .content-area { flex: 1; display: flex; flex-direction: column; position: relative; }
              iframe { width: 100%; height: 100%; border: none; }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="title">${filename}</div>
              <button id="custom-download-btn" class="download-btn">
                <svg viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
                Download PDF
              </button>
            </div>
            <div class="content-area">
              <iframe src="${pdfUrl}"></iframe>
            </div>
          </body>
          </html>
        `);
        pdfWindow.document.close();

        // Safely add the click listener from our origin context to force the download name
        const downloadBtn = pdfWindow.document.getElementById(
          "custom-download-btn",
        );
        if (downloadBtn) {
          downloadBtn.addEventListener("click", () => {
            const a = document.createElement("a");
            a.href = pdfUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          });
        }
      } else {
        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error("View annotation PDF error:", err);
      swalError("Failed to fetch annotation PDF");
    } finally {
      setIsViewingAnnotatedPdf(false);
    }
  };

  const reportRef = useRef(null);

  const handlePrintReport = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Score Report - ${testInfo.name} - ${studentInfo.name}`,
    pageStyle: `
      @page { 
        size: A4 landscape; 
        margin: 5mm;
      }
      @media print {
        body { 
          -webkit-print-color-adjust: exact; 
        }
      }
    `,
  });

  const handlePrintSummary = useReactToPrint({
    contentRef: summaryRef,
    documentTitle: `Performance Report - ${testInfo.name} - ${studentInfo.name}`,
    pageStyle: `
      @page { 
        size: A4 landscape; 
        margin: 10mm;
        align: center;
      }
      @media print {
        body { 
          -webkit-print-color-adjust: exact; 
        }
      }
    `,
  });

  // Fetch Logic
  const fetchResults = useCallback(async () => {
    if (role === "Instructor" || role === "Admin") {
      const manualReviewParam = searchParams.get("mr") === "true";
      setManualReview(manualReviewParam);
    }

    if (!answerPaperId) {
      setError("Invalid URL: No answer paper ID found");
      setLoading(false);
      return;
    }
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/get/${answerPaperId}`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch results");
      }
      setScoreData(data.score);
      if (data.classId) setClassId(data.classId);
    } catch (err) {
      setError(err.message || "Failed to fetch results");
      swalError(err.message || "Failed to fetch results");
    } finally {
      setLoading(false);
    }
  }, [answerPaperId, searchParams, role, serverUrl]);

  useEffect(() => {
    fetchResults();

    fetchWithAuth(
      `${serverUrl}/api/breadcrumbs/get?answerPaperId=${answerPaperId}`,
      {
        credentials: "include",
      },
    )
      .then((res) => {
        if (!res) return;
        return res.json();
      })
      .then((data) => {
        if (data.class?.id) {
          setClassInfo({
            name: data.class.name,
            id: data.class.id,
            instructorName: data.class.instructorName || "",
          });
        } else {
          setClassInfo({ id: null, name: "Unknown Class", instructorName: "" });
        }
        if (data.test?.id) {
          setTestInfo((prev) => ({
            ...prev,
            name: data.test.name,
            id: data.test.id,
            isPracticeTest: data.test.isPracticeTest || false,
            templateId: data.test.standardizedTestTemplateId,
            annotationPrintingOffsetConfigId:
              data.test.annotationPrintingOffsetConfigId ||
              prev.annotationPrintingOffsetConfigId ||
              null,
          }));
        } else {
          setTestInfo((prev) => ({
            ...prev,
            id: null,
            name: "Unknown Test",
            isPracticeTest: false,
          }));
        }
      })
      .catch((err) => {
        setClassInfo({ id: null, name: "Unknown Class", instructorName: "" });
      });
  }, [answerPaperId, serverUrl, fetchResults]);

  useEffect(() => {
    const fetchAnswerPaperAndStudent = async () => {
      try {
        const res = await fetchWithAuth(
          `${serverUrl}/api/answer-paper/get/${answerPaperId}`,
          { credentials: "include" },
        );

        if (!res) return;

        const data = await res.json();

        if (!res.ok) {
          throw new Error(
            data.message || "Failed to fetch answer paper details.",
          );
        }

        setAnswerPaperURL(data.url);
        setAnswerPaperComments(data.comments || "");
        setAnswerPaperStatus(data.status || "");
        if (
          data.proctoringLog?.violations?.length > 0 ||
          data.proctoringLog?.snapshots?.length > 0
        ) {
          setProctoringLog(data.proctoringLog);
        }

        if (role === "Student") {
          const fullName = user
            ? `${user.firstName || ""} ${user.lastName || ""}`
            : "Unknown Student";
          setStudentInfo({
            id: user?._id || null,
            referenceId: user?.referenceId || "",
            name: fullName.trim() || "Unknown Student",
          });
        } else if (data.studentId) {
          const studentRes = await fetchWithAuth(
            `${serverUrl}/api/user/get/${data.studentId}`,
            { credentials: "include" },
          );

          if (!studentRes) return;
          const studentData = await studentRes.json();

          if (!studentRes.ok) {
            throw new Error(
              studentData.message || "Failed to fetch student details.",
            );
          }

          const fullName = `${studentData.firstName || ""} ${studentData.lastName || ""}`;

          setStudentInfo({
            id: studentData._id || null,
            referenceId: studentData.referenceId || "",
            name: fullName.trim() || "Unknown Student",
          });
        }
      } catch (err) {
        if (role !== "Student") {
          setStudentInfo({
            id: null,
            referenceId: "",
            name: "Unknown Student",
          });
        }
      }
    };
    fetchAnswerPaperAndStudent();
  }, [answerPaperId, serverUrl, role, user]);

  useEffect(() => {
    const fetchTestPaperURL = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/test/get/${testInfo.id}`,
          {
            credentials: "include",
          },
        );
        if (!response) return;
        const data = await response.json();
        if (data.classId) setClassId(data.classId);
        setTestPaperURL(data.testPaperURL);
        setTestInfo((prev) => ({
          ...prev,
          isPracticeTest: data.isPracticeTest || false,
          templateId: data.standardizedTestTemplateId,
          annotationPrintingOffsetConfigId:
            data.annotationPrintingOffsetConfigId ||
            prev.annotationPrintingOffsetConfigId ||
            null,
        }));
      } catch (error) {
        swalError("Failed to fetch test paper URL");
      }
    };

    if (testInfo.id) {
      fetchTestPaperURL();
    }
  }, [serverUrl, testInfo.id]);

  // Fetch Certificate for student and test if issued
  useEffect(() => {
    const fetchCertificate = async () => {
      const targetTestId = testInfo.id;
      const targetStudentId =
        studentInfo.id || (role === "Student" ? user?._id : null);
      if (!targetTestId || !targetStudentId) return;

      try {
        const res = await certificateService.getIssuedCertificates({
          testId: targetTestId,
          studentId: targetStudentId,
        });
        if (
          res &&
          res.success &&
          Array.isArray(res.data) &&
          res.data.length > 0
        ) {
          const validCert = res.data.find((c) => !c.isRevoked);
          if (validCert) {
            setIssuedCertificate(validCert);
          }
        }
      } catch (err) {
        console.warn(
          "Failed to fetch issued certificate for results view:",
          err,
        );
      }
    };

    fetchCertificate();
  }, [testInfo.id, studentInfo.id, user?._id, role]);

  const handleDownloadCertificate = async () => {
    if (!issuedCertificate) return;
    setIsDownloadingCert(true);
    try {
      const name =
        studentInfo.name ||
        `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
        "Certificate";
      const sanitizedName = name.replace(/[^a-zA-Z0-9]/g, "_");
      const templateTitle = (
        issuedCertificate.templateName ||
        issuedCertificate.templateId?.name ||
        "Certificate"
      ).replace(/[^a-zA-Z0-9]/g, "_");
      const filename = `${templateTitle}_${sanitizedName}.png`;
      await certificateService.downloadCertificate(
        issuedCertificate._id,
        filename,
      );
    } catch (err) {
      console.error("Certificate download error:", err);
      swalError(err.message || "Failed to download certificate.");
    } finally {
      setIsDownloadingCert(false);
    }
  };

  // Student specific logic
  useEffect(() => {
    if (role === "Student" && scoreData?.result) {
      let hasRequestedRegrade = scoreData.result.some((question) => {
        return question.regradeRequestDate;
      });
      setIsRegradeRequestSubmitted(hasRequestedRegrade);

      const pending = scoreData.result.filter((question) => {
        return question.isRegradeRequested && !question.regradeRequestDate;
      });
      setPendingRegradeRequests(pending);

      const submitted = scoreData.result.filter((question) => {
        return !!question.regradeRequestDate;
      });
      setSubmittedRegradeRequests(submitted);
    }
  }, [scoreData?.result, role]);

  // Instructor specific logic
  useEffect(() => {
    if (role === "Instructor" || role === "Admin") {
      if (
        !hasShownReviewWarning.current &&
        answerPaperStatus === "Review" &&
        scoreData?.result &&
        Array.isArray(scoreData.result)
      ) {
        const hasManualReviewQuestion = scoreData.result.some(
          (q) => q.isManualReview === true,
        );
        if (!hasManualReviewQuestion && answerPaperComments) {
          hasShownReviewWarning.current = true;
          swalWarning("", {
            html: `<div style="text-align: left;">${answerPaperComments}<br/><br/>Please review the feedback below and adjust the scores for any questions that do not match the test paper.</div>`,
            title: "Review Required",
            timer: null,
            timerProgressBar: false,
            showConfirmButton: true,
            confirmButtonText: "Got it",
          });
        }
      }
    }
  }, [answerPaperStatus, scoreData, answerPaperComments, role]);

  useEffect(() => {
    if (currentEditingQuestion) {
      const isQuestionNumberChanged =
        parseFloat(editForm.questionNumber) !==
        currentEditingQuestion.questionNumber;
      const isStudentAnswerChanged =
        editForm.studentAnswer !== currentEditingQuestion.studentAnswer;
      const isObtainedScoreChanged =
        parseFloat(editForm.obtainedScore) !==
        currentEditingQuestion.obtainedScore;
      const isMaxScoreChanged =
        parseFloat(editForm.maxScore) !== currentEditingQuestion.maxScore;
      const isFeedbackChanged =
        editForm.feedback !== currentEditingQuestion.feedback;
      const isManualReviewChanged =
        editForm.isManualReview !== currentEditingQuestion.isManualReview;

      setIsSubmitEnabled(
        isQuestionNumberChanged ||
          isStudentAnswerChanged ||
          isObtainedScoreChanged ||
          isMaxScoreChanged ||
          isFeedbackChanged ||
          isManualReviewChanged,
      );
    }
  }, [editForm, currentEditingQuestion]);

  // Actions
  const formatInfoTime = (seconds) => {
    if (
      seconds === null ||
      seconds === undefined ||
      isNaN(seconds) ||
      seconds === ""
    ) {
      return "NA";
    }
    const rounded = Math.round(Number(seconds));
    if (isNaN(rounded) || rounded < 0) return "NA";
    if (rounded < 60) return `${rounded}s`;
    const mins = Math.floor(rounded / 60);
    const remainingSecs = rounded % 60;
    return remainingSecs > 0
      ? `${mins}m ${remainingSecs}s (${rounded}s)`
      : `${mins}m (${rounded}s)`;
  };

  const handleOpenInfo = (question) => {
    const isOnline =
      question.source === "Graded Online Exam" ||
      question.avgCorrectTimeTaken !== undefined;
    setInfoData({
      source: question.source || "",
      reasoning: question.reasoning || "",
      isOnlineExam: isOnline,
      timeTaken:
        question.timeTaken !== undefined
          ? question.timeTaken
          : question.timeTake,
      avgCorrectTimeTaken: question.avgCorrectTimeTaken,
    });
    setIsInfoModalOpen(true);
  };

  const handleEditClick = (question) => {
    setCurrentEditingQuestion(question);
    setEditForm({
      questionNumber: question.questionNumber,
      studentAnswer: question.studentAnswer,
      obtainedScore: question.obtainedScore,
      maxScore: question.maxScore,
      feedback: question.feedback,
      isManualReview: question.isManualReview,
      scoreChangeReason: question.obtainedScoreLogs?.scoreChangeReason || "",
    });
    setIsModalOpen(true);
    setIsSubmitEnabled(false);
  };

  const handleFormChange = (e) => {
    const { name, type, value, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setEditForm({ ...editForm, [name]: newValue });
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!currentEditingQuestion) return;

    setUpdateLoading(true);
    try {
      const feedbackText =
        editForm.feedback !== currentEditingQuestion.feedback
          ? `${editForm.feedback} (by Instructor)`
          : editForm.feedback;

      const newScoreLog = {
        previousObtainedScore: currentEditingQuestion.obtainedScore,
        scoreChangeReason: editForm.scoreChangeReason,
      };

      const response = await fetchWithAuth(
        `${serverUrl}/api/score/update/${scoreData._id}/${currentEditingQuestion._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            questionNumber: editForm.questionNumber,
            studentAnswer: editForm.studentAnswer,
            obtainedScore: editForm.obtainedScore,
            maxScore: editForm.maxScore,
            feedback: feedbackText,
            isManualReview: editForm.isManualReview,
            ...(newScoreLog && { obtainedScoreLogs: newScoreLog }),
          }),
        },
      );
      if (!response) return;

      if (!response.ok) {
        throw new Error("Failed to update score");
      }

      let allQuestionsReviewed = false;
      setScoreData((prevData) => {
        if (!prevData || !prevData.result) return prevData;

        const updatedResult = prevData.result.map((item) => {
          if (item._id === currentEditingQuestion._id) {
            const updatedLogs = {
              previousObtainedScore: currentEditingQuestion.obtainedScore,
              scoreChangeReason: editForm.scoreChangeReason,
            };

            return {
              ...item,
              questionNumber: editForm.questionNumber,
              studentAnswer: editForm.studentAnswer,
              obtainedScore: parseFloat(editForm.obtainedScore),
              maxScore: parseFloat(editForm.maxScore),
              feedback: feedbackText,
              isManualReview: editForm.isManualReview,
              ...(updatedLogs && { obtainedScoreLogs: updatedLogs }),
            };
          }
          return item;
        });

        allQuestionsReviewed = !updatedResult.some(
          (q) => q.isManualReview === true,
        );
        return { ...prevData, result: updatedResult };
      });

      setIsModalOpen(false);

      if (allQuestionsReviewed && manualReview) {
        try {
          const apResponse = await fetchWithAuth(
            `${serverUrl}/api/answer-paper/get/${answerPaperId}`,
            { credentials: "include" },
          );
          if (apResponse && apResponse.ok) {
            const apData = await apResponse.json();
            setAnswerPaperStatus(apData.status || "");
            setAnswerPaperComments(apData.comments || "");
          }
        } catch (error) {
          swalError("Failed to update answer paper status");
        }
        setManualReview(false);
        swalSuccess(
          "All questions have been reviewed and adjusted successfully!",
        );
      } else {
        swalSuccess("Score updated successfully");
      }
    } catch (err) {
      swalError("Failed to update score. Please try again.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteResult = async (questionId) => {
    const currentScoreId = scoreDataRef.current?._id || scoreData?._id;
    if (!currentScoreId) {
      swalError("Score data not loaded yet.");
      return;
    }

    swalConfirm(
      "Are you sure you want to delete this question from the score?",
      "It will impact the result of this test for the student!",
      "Yes, Continue!",
      "No, Cancel",
    ).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetchWithAuth(
            `${serverUrl}/api/score/deleteQuestion/${currentScoreId}/${questionId}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );
          if (!response) return;
          const responseInfo = await response.json();

          if (!response.ok) {
            swalError(
              responseInfo.message || "Failed to delete score of a question",
            );
            return;
          }

          setScoreData((prevData) => ({
            ...prevData,
            result: prevData.result.filter((q) => q._id !== questionId),
          }));
          swalSuccess(
            responseInfo.message || "Score of a question deleted successfully",
          );
        } catch (err) {
          swalError(err.message || "Failed to delete score of a question");
        }
      }
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentEditingQuestion(null);
  };

  const handleOpenAnswerPaper = () => {
    if (!answerPaperURL || answerPaperURL === "") {
      swalError("Answer paper file not available.");
      return;
    }
    window.open(answerPaperURL, "_blank");
  };

  const handleOpenTestPaper = () => {
    if (!testPaperURL || testPaperURL === "") {
      swalError("Test paper file not available.");
      return;
    }
    window.open(testPaperURL, "_blank");
  };

  const downloadCSV = () => {
    const hasSubjectData = scoreData.result.some((q) => q.subject);
    const header = [
      "Question Number",
      ...(hasSubjectData ? ["Subject", "Section"] : []),
      "Question Text",
      "Student's Answer",
      "Obtained Score",
      "Max Score",
      "Feedback",
    ];
    const rows = scoreData.result.map((q) => [
      q.questionNumber,
      ...(hasSubjectData
        ? [
            `"${(q.subject || "").replace(/"/g, '""')}"`,
            `"${(q.sectionNumber || "").replace(/"/g, '""')}"`,
          ]
        : []),
      `"${(q.questionText || "").replace(/"/g, '""')}"`,
      `"${(getMCQOptionText(q, q.studentAnswer) || "").replace(/"/g, '""')}"`,
      q.obtainedScore,
      q.maxScore,
      `"${(q.feedback || "").replace(/"/g, '""')}"`,
    ]);
    let csvContent = header.join(",") + "\r\n";
    rows.forEach((row) => {
      csvContent += row.join(",") + "\r\n";
    });
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Score Report - ${testInfo.name} - ${studentInfo.name}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    swalSuccess("CSV file downloaded successfully");
  };

  // Student specific actions
  const handleOpenRegradeCommentModal = (question) => {
    const questionId = question._id;
    const regradeComment = question.regradeComment || "";
    const { questionNumber, questionText, studentAnswer, feedback } = question;
    setIsRegradeCommentModalOpen(true);
    setCurrentRegradeRequest({
      questionId,
      questionNumber,
      questionText,
      studentAnswer: getMCQOptionText(question, studentAnswer),
      feedback,
      regradeComment,
    });
  };

  const handleCloseRegradeCommentModal = () => {
    setIsRegradeCommentModalOpen(false);
    setCurrentRegradeRequest(initialRegradeRequestState);
  };

  const handleCurrentRegradeRequestChange = (e) => {
    const { name, value } = e.target;
    setCurrentRegradeRequest((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitRegradeRequest = async () => {
    swalConfirm(
      "Are you sure you want to send regrade request to the instructor?",
      "You can only do it once!",
      "Yes, Continue!",
      "No, Cancel",
    ).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        setError(null);
        try {
          const response = await fetchWithAuth(
            `${serverUrl}/api/score/submit-regrade-request/${scoreData._id}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
            },
          );
          if (!response) return;
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.message || "Failed to submit regrade request");
          }
          swalSuccess("Regrade request submitted successfully.");
          fetchResults();
        } catch (err) {
          swalError(err.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleRegradeCommentSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (!currentRegradeRequest.regradeComment) {
        swalError("Please enter a comment for the regrade request.");
        return;
      }
      const payload = [currentRegradeRequest];
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/save-regrade-comments/${scoreData._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "include",
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to save regrade request");
      }
      swalSuccess(data.message || "Regrade request saved successful.");
      handleCloseRegradeCommentModal();
      fetchResults();
    } catch (err) {
      swalError(err.message || "Failed to save regrade request");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Instructor specific actions for Answer paper
  const handleUpdateComments = async (e) => {
    e.preventDefault();
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/answer-paper/update/${answerPaperId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ comments: answerPaperComments }),
        },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to update comments");
      swalSuccess("Comments updated successfully!");
      setIsCommentsModalOpen(false);
    } catch (error) {
      swalError("Failed to update comments. Please try again.");
    }
  };

  // Summary Modals logic
  const handleViewSummary = async () => {
    if (role === "Student") {
      if (scoreData?.summary) {
        setIsSummaryModalOpen(true);
      } else {
        swalError("No Performance Report exists");
      }
    } else {
      setIsSummaryModalOpen(true);
      setIsSummaryEditing(false);
      setOnSaveSummary(false);

      if (!scoreData?.summary) {
        setIsSummaryGenerating(true);
        setTimeout(() => setIsSummaryGenerating(false), 800);
      }
    }
  };

  const handleCloseSummaryModal = () => {
    setIsSummaryModalOpen(false);
    setIsSummaryEditing(false);
    setOnSaveSummary(false);
  };

  const handleEditSummary = () => {
    setIsSummaryEditing(true);
    setOnSaveSummary(false);
  };

  const handleCancelEditSummary = () => {
    setIsSummaryEditing(false);
    setOnSaveSummary(false);
  };

  const onSaveSummaryClick = () => setOnSaveSummary(true);

  const handleSaveSummary = async (updatedSummary) => {
    setOnSaveSummary(false);
    setIsSummarySaving(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/update-summary/${scoreData._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            summary: {
              overallPerformance: updatedSummary.overallPerformance,
              strengths: updatedSummary.strengths,
              weaknesses: updatedSummary.weaknesses,
              recommendations: updatedSummary.recommendations,
            },
          }),
        },
      );
      if (!response) return;

      if (!response.ok) {
        const text = await response.text();
        let errorMessage = "Failed to update summary";
        try {
          const data = JSON.parse(text);
          errorMessage = data.message || errorMessage;
        } catch (e) {
          errorMessage = `Server Error (${response.status})`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setScoreData((prev) => ({ ...prev, summary: data.updatedSummary }));
      swalSuccess(data.message || "Successfully updated summary");
    } catch (error) {
      swalError(error.message);
    } finally {
      setIsSummarySaving(false);
      setIsSummaryEditing(false);
    }
  };

  const handleDeleteSummary = async () => {
    const confirm = await swalConfirm(
      "Are you sure?",
      "Do you want to delete this performance report? This action cannot be undone.",
      "Yes, delete it!",
      "No, keep it",
    );

    if (confirm.isConfirmed) {
      setIsSummarySaving(true);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/score/update-summary/${scoreData._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ summary: null }),
          },
        );
        if (!response) return;
        if (!response.ok) {
          throw new Error("Failed to delete summary");
        }
        setScoreData((prev) => ({ ...prev, summary: null }));
        swalSuccess("Performance report deleted successfully");
        setIsSummaryModalOpen(false);
      } catch (error) {
        swalError(error.message);
      } finally {
        setIsSummarySaving(false);
      }
    }
  };

  // Subject / Section structure (mirroring StudentExamMode.jsx pattern)
  const subjectStructure = useMemo(() => {
    if (!scoreData?.result?.length) return { subjects: [], subjectMap: {} };
    const subjectMap = {};
    scoreData.result.forEach((q) => {
      const subj = q.subject || "";
      const sec = q.sectionNumber || "";
      if (!subj) return; // skip if no subject
      if (!subjectMap[subj])
        subjectMap[subj] = {
          sections: {},
          order: Object.keys(subjectMap).length,
        };
      if (sec && !subjectMap[subj].sections[sec])
        subjectMap[subj].sections[sec] = {
          questions: [],
          order: Object.keys(subjectMap[subj].sections).length,
        };
      if (sec) subjectMap[subj].sections[sec].questions.push(q);
    });
    const subjects = Object.keys(subjectMap).sort(
      (a, b) => subjectMap[a].order - subjectMap[b].order,
    );
    return { subjects, subjectMap };
  }, [scoreData?.result]);

  const hasSubjects = subjectStructure.subjects.length > 0;

  // Per-subject score stats
  const subjectStats = useMemo(() => {
    const statsMap = {};
    subjectStructure.subjects.forEach((subj) => {
      let obtained = 0,
        max = 0,
        correctCount = 0,
        incorrectCount = 0;
      const secMap = subjectStructure.subjectMap[subj].sections;
      Object.values(secMap).forEach((sec) => {
        sec.questions.forEach((q) => {
          obtained += q.obtainedScore || 0;
          max += q.maxScore || 0;
          if (q.obtainedScore === q.maxScore) {
            correctCount++;
          } else {
            incorrectCount++;
          }
        });
      });
      // Also count questions that have this subject but no sectionNumber
      if (scoreData?.result) {
        scoreData.result.forEach((q) => {
          if (q.subject === subj && !q.sectionNumber) {
            obtained += q.obtainedScore || 0;
            max += q.maxScore || 0;
            if (q.obtainedScore === q.maxScore) {
              correctCount++;
            } else {
              incorrectCount++;
            }
          }
        });
      }
      statsMap[subj] = { obtained, max, correctCount, incorrectCount };
    });
    return statsMap;
  }, [subjectStructure, scoreData?.result]);

  // Effective subject (null = show all, no default selection)
  const effectiveSubject = useMemo(() => {
    if (activeSubject && subjectStructure.subjectMap[activeSubject])
      return activeSubject;
    return null;
  }, [activeSubject, subjectStructure]);

  // Effective section (defaults to first section)
  const effectiveSection = useMemo(() => {
    if (!effectiveSubject || !subjectStructure.subjectMap[effectiveSubject])
      return null;
    const secMap = subjectStructure.subjectMap[effectiveSubject].sections;
    const sections = Object.keys(secMap).sort(
      (a, b) => secMap[a].order - secMap[b].order,
    );
    if (sections.length === 0) return null;
    if (activeSection && sections.includes(activeSection)) return activeSection;
    return sections[0];
  }, [activeSection, effectiveSubject, subjectStructure]);

  // Filtered result based on active subject and section
  const filteredResult = useMemo(() => {
    if (!scoreData?.result) return [];
    if (!hasSubjects || !effectiveSubject) return scoreData.result;

    let filtered = scoreData.result.filter(
      (q) => q.subject === effectiveSubject,
    );
    if (effectiveSection) {
      filtered = filtered.filter(
        (q) => !q.sectionNumber || q.sectionNumber === effectiveSection,
      );
    }
    return filtered;
  }, [scoreData?.result, effectiveSubject, effectiveSection, hasSubjects]);

  // Determine if this is an online examination result
  const isOnlineExamination = useMemo(() => {
    if (!scoreData?.result?.length) return false;
    return scoreData.result.some(
      (q) =>
        q.source === "Graded Online Exam" ||
        q.avgCorrectTimeTaken !== undefined,
    );
  }, [scoreData?.result]);

  const isStudentOnlineExam = useMemo(() => {
    return (
      (role === "Student" || role?.toLowerCase() === "student") &&
      isOnlineExamination
    );
  }, [role, isOnlineExamination]);

  // Set default tab to "normal" (per-question view) for students in online exams
  useEffect(() => {
    if (isStudentOnlineExam) {
      setActiveTab("normal");
    }
  }, [isStudentOnlineExam]);

  // Pre-compute palette question statuses + stats (avoid recalc on every render)
  const paletteData = useMemo(() => {
    if (!filteredResult?.length)
      return {
        statuses: [],
        counts: { correct: 0, incorrect: 0, unanswered: 0 },
      };
    let correct = 0,
      incorrect = 0,
      unanswered = 0;
    const statuses = filteredResult.map((q) => {
      const sAns = (q.studentAnswer || "").trim().toUpperCase();
      const qType = (q.type || "MCQ").toUpperCase();
      const cAns =
        qType === "FILL-IN-THE-BLANKS" || qType === "NUMERICAL"
          ? (q.correctAnswer || "").trim().toUpperCase()
          : q.correctOptions && q.correctOptions.length > 0
            ? q.correctOptions[0].trim().toUpperCase()
            : "";
      const isU = !sAns;
      const isC = sAns !== "" && sAns === cAns;
      if (isU) unanswered++;
      else if (isC) correct++;
      else incorrect++;
      return { isCorrect: isC, isUnanswered: isU };
    });
    return { statuses, counts: { correct, incorrect, unanswered } };
  }, [filteredResult]);

  const totalScore = useMemo(() => {
    if (!scoreData?.result) return { obtained: 0, max: 0 };
    const totalObtained = scoreData.result.reduce(
      (sum, q) => sum + q.obtainedScore,
      0,
    );
    const totalMax = scoreData.result.reduce((sum, q) => sum + q.maxScore, 0);
    return { obtained: totalObtained, max: totalMax };
  }, [scoreData?.result]);

  // Reusable question card renderer for the "normal" (card) view
  const renderQuestionCard = useCallback(
    (question) => (
      <>
        <div className="question-header">
          <h2>{question.questionNumber}</h2>
          {question.subject && (
            <span
              className="results-subject-badge"
              style={{ marginLeft: "0.5rem" }}
            >
              {question.subject}
            </span>
          )}
          {question.sectionNumber && (
            <span
              className="results-section-badge"
              style={{ marginLeft: "0.35rem" }}
            >
              {question.sectionNumber}
            </span>
          )}
          <div className="score-badge">
            {question.obtainedScore}/{question.maxScore}
          </div>
          {(role === "Instructor" || role === "Admin") && (
            <button
              className="btn-edit"
              onClick={() => handleEditClick(question)}
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
          )}
        </div>
        <div className="question-content">
          <div className="question-text" style={{ marginBottom: "1rem" }}>
            <LazyMarkdown>{question.questionText || ""}</LazyMarkdown>
          </div>
          {question.diagramFileURL && (
            <div
              className="diagram-viewer-container"
              style={{
                marginTop: "0.5rem",
                marginBottom: "1rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "0.25rem",
              }}
            >
              <div
                style={{
                  position: "relative",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: "#f8fafc",
                  maxWidth: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
                  cursor: "pointer",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxUrl(question.diagramFileURL);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.02)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow =
                    "0 1px 3px 0 rgba(0, 0, 0, 0.05)";
                }}
                title="Click to view full size image"
              >
                <img
                  src={question.diagramFileURL}
                  alt={`Diagram for Question ${question.questionNumber}`}
                  style={{
                    maxHeight: "220px",
                    maxWidth: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: "0.7rem",
                  color: "#64748b",
                  fontStyle: "italic",
                  marginTop: "0.2rem",
                }}
              >
                Click image to view in new tab
              </span>
            </div>
          )}
          {question.options && (
            <div
              className="options-section"
              style={{ marginBottom: "1rem", color: "#555" }}
            >
              <LazyMarkdown>
                {Array.isArray(question.options)
                  ? question.options
                      .map((opt) =>
                        typeof opt === "object"
                          ? `${opt.key || ""}) ${opt.text || ""}`
                          : String(opt),
                      )
                      .join("\n\n")
                  : String(question.options).replace(
                      /\s([a-dA-D]\))/g,
                      "\n\n$1",
                    )}
              </LazyMarkdown>
            </div>
          )}
          <div className="answer-section">
            <div className="student-answer">
              <label>Student's Answer:</label>
              <p>{getMCQOptionText(question, question.studentAnswer)}</p>
            </div>
          </div>
          <div className="feedback-section">
            <label>{isOnlineExamination ? "Solution:" : "Feedback:"}</label>
            <p>{question.feedback}</p>
          </div>
        </div>
      </>
    ),
    [role, isOnlineExamination],
  );

  // Columns definition (memoized to prevent re-creation on every render)
  const columns = useMemo(() => {
    const cols = [
      { key: "questionNumber", label: "Question Number", sortable: true },
      {
        key: "questionText",
        label: "Question Text",
        sortable: false,
        render: (item) => (
          <div
            style={{
              maxWidth: "300px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <LazyMarkdown components={SPAN_COMPONENTS}>
              {item.questionText || "-"}
            </LazyMarkdown>
          </div>
        ),
      },
      {
        key: "studentAnswer",
        label: "Student's Answer",
        sortable: false,
        render: (item) => (
          <div
            style={{
              maxWidth: "300px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <LazyMarkdown components={SPAN_COMPONENTS}>
              {formatLatexText(getMCQOptionText(item, item.studentAnswer)) ||
                "-"}
            </LazyMarkdown>
          </div>
        ),
      },
      { key: "obtainedScore", label: "Obtained Score", sortable: true },
      { key: "maxScore", label: "Max Score", sortable: true },
      {
        key: "feedback",
        label: isOnlineExamination ? "Solution" : "Feedback",
        sortable: false,
        render: (item) => (
          <div
            style={{
              maxWidth: "300px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <LazyMarkdown components={SPAN_COMPONENTS}>
              {item.feedback || "-"}
            </LazyMarkdown>
          </div>
        ),
      },
      {
        key: "info",
        label: "",
        render: (item) => (
          <button
            className="delete-btn"
            onClick={() => handleOpenInfo(item)}
            title="View Info"
          >
            <i
              className="fa-solid fa-circle-info"
              style={{ fontSize: "1.2rem" }}
            ></i>
          </button>
        ),
        sortable: false,
      },
    ];

    if (role === "Instructor" || role === "Admin") {
      cols.push(
        {
          key: "edit",
          label: "",
          render: (item) => (
            <button
              className="delete-btn"
              onClick={() => handleEditClick(item)}
            >
              <img src="/imgs/pencil_icon.svg" alt="edit-btn" width={20} />
            </button>
          ),
          sortable: false,
        },
        {
          key: "delete",
          label: "",
          render: (item) => (
            <button
              className="delete-btn"
              onClick={() => handleDeleteResult(item._id)}
            >
              <img src="/imgs/trash_icon.svg" alt="delete-btn" width={20} />
            </button>
          ),
          sortable: false,
        },
      );
    } else if (role === "Student") {
      cols.push({
        key: "regradeElement",
        label: "Regrade",
        render: (question) => {
          const isCommented =
            question.regradeComment && question.regradeComment !== "";
          const isInstructorAction =
            !question.isRegradeRequested && question.regradeRequestDate;
          const instructorAction = isInstructorAction ? "Regraded" : "Pending";

          return (
            <>
              {isViewingRegradeRequests && <p>{instructorAction}</p>}
              {isCommented ? (
                <button
                  className="preview-btn"
                  onClick={() => handleOpenRegradeCommentModal(question)}
                >
                  <img src="/imgs/eye_icon.svg" alt="preview-btn" width={30} />
                </button>
              ) : (
                <button
                  className="delete-btn"
                  onClick={() => handleOpenRegradeCommentModal(question)}
                >
                  <img src="/imgs/pencil_icon.svg" alt="edit-btn" width={20} />
                </button>
              )}
            </>
          );
        },
        sortable: false,
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, isOnlineExamination, isViewingRegradeRequests, scoreData?._id]);

  // Breadcrumbs (memoized)
  const breadcrumbs = useMemo(() => {
    if (role === "Student") {
      if (testInfo.isPracticeTest) {
        return [
          { name: "Home", path: "/student" },
          {
            name: `${testInfo.name}`,
            path: `/student/practice-tests`,
            state: { activeTab: testInfo.templateId },
          },
          {
            name: "Results",
            path: `/student/results/${answerPaperId}`,
            isLast: true,
          },
        ];
      }
      return [
        { name: "Home", path: "/student" },
        {
          name: `${testInfo.name}`,
          path: `/student/view-class/${classInfo.id}`,
        },
        {
          name: "Results",
          path: `/student/results/${answerPaperId}`,
          isLast: true,
        },
      ];
    }
    return [
      { name: "Home", path: "/instructor" },
      {
        name: `${classInfo.name}`,
        path: `/instructor/manage-class/${classInfo.id}`,
      },
      {
        name: `${testInfo.name}`,
        path: `/instructor/test-grading/${testInfo.id}`,
      },
      {
        name: "Results",
        path: `/instructor/results/${answerPaperId}`,
        isLast: true,
      },
    ];
  }, [
    role,
    testInfo.name,
    testInfo.id,
    testInfo.isPracticeTest,
    testInfo.templateId,
    classInfo.name,
    classInfo.id,
    answerPaperId,
  ]);

  if (loading) {
    return (
      <div className="results-page-wrapper">
        <Navbar links={[]} breadcrumbs={breadcrumbs} />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 80px)",
          }}
        >
          <Loader
            style={{
              fontSize: "50px",
              position: "static",
              left: "auto",
              top: "auto",
            }}
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-page-wrapper">
        <Navbar links={[]} breadcrumbs={breadcrumbs} />
        <div
          className="error"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 80px)",
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  if (!scoreData) {
    return (
      <div className="results-page-wrapper">
        <Navbar links={[]} breadcrumbs={breadcrumbs} />
        <div
          className="no-data"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 80px)",
          }}
        >
          No results found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`results-page-wrapper ${isStudentOnlineExam || activeTab === "normal" || activeTab === "annotation" ? "exam-fullscreen-mode" : ""}`}
    >
      <Navbar links={[]} breadcrumbs={breadcrumbs} />
      {/* Invisible results report content*/}
      <div style={{ display: "none" }}>
        <Report
          ref={reportRef}
          testName={testInfo.name}
          studentId={studentInfo.referenceId}
          studentName={studentInfo.name}
          resultData={scoreData.result}
        />
      </div>
      {hasSubjects && (isStudentOnlineExam || activeTab === "normal") && (
        <div
          className={`exam-subject-nav ${isMobileScoreNavExpanded ? "mobile-expanded" : "mobile-collapsed"}`}
          style={{
            width: "100%",
            margin: 0,
            borderBottom: "1px solid #e2e8f0",
            background: "#fff",
          }}
        >
          {/* Mobile Collapsible Summary Bar (visible on mobile only) */}
          <div
            className="results-mobile-subject-bar"
            onClick={() => setIsMobileScoreNavExpanded((prev) => !prev)}
            role="button"
            tabIndex={0}
          >
            <div className="results-mobile-subject-info">
              <span className="results-mobile-subject-icon">
                <i className="fa-solid fa-chart-simple" />
              </span>
              <span className="results-mobile-subject-title">
                {!activeSubject ? "Total Score" : effectiveSubject}
              </span>
              <span className="results-mobile-subject-badge">
                {!activeSubject
                  ? `${totalScore.obtained}/${totalScore.max}`
                  : `${subjectStats[effectiveSubject]?.obtained || 0}/${subjectStats[effectiveSubject]?.max || 0}`}
              </span>
              {effectiveSection && (
                <span className="results-mobile-section-badge">
                  {effectiveSection}
                </span>
              )}
            </div>
            <div className="results-mobile-subject-actions">
              <button
                type="button"
                className="results-mobile-expand-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMobileScoreNavExpanded((prev) => !prev);
                }}
                aria-label={
                  isMobileScoreNavExpanded
                    ? "Collapse subjects"
                    : "Expand subjects"
                }
              >
                <i
                  className={`fa-solid fa-chevron-${isMobileScoreNavExpanded ? "up" : "down"} results-mobile-chevron`}
                />
              </button>
            </div>
          </div>

          {/* Expandable Navigation Content */}
          <div
            className={`results-subject-nav-content ${
              isMobileScoreNavExpanded ? "expanded" : "collapsed"
            }`}
          >
            <div
              className="exam-subject-nav-inner"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 20px",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <div
                className="exam-subject-tabs"
                style={{
                  background: "transparent",
                  padding: 0,
                  margin: 0,
                  flex: 1,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                }}
              >
                <button
                  type="button"
                  className={`exam-subject-tab ${!activeSubject ? "active" : ""}`}
                  onClick={() => {
                    setCurrentOEIndex(0);
                    setActiveSubject("");
                    setActiveSection("");
                  }}
                >
                  <span className="exam-subject-tab-name">Total Score</span>
                  <span className="exam-subject-tab-count">
                    {totalScore.obtained}/{totalScore.max}
                  </span>
                </button>
                {subjectStructure.subjects.map((subj) => {
                  const stats = subjectStats[subj];
                  const isActive =
                    effectiveSubject === subj && activeSubject !== "";
                  return (
                    <button
                      key={subj}
                      type="button"
                      className={`exam-subject-tab ${isActive ? "active" : ""}`}
                      onClick={() => {
                        setCurrentOEIndex(0);
                        if (activeSubject !== subj) {
                          setActiveSubject(subj);
                          const secMap =
                            subjectStructure.subjectMap[subj]?.sections || {};
                          const sections = Object.keys(secMap).sort(
                            (a, b) => secMap[a].order - secMap[b].order,
                          );
                          setActiveSection(sections[0] || "");
                        }
                      }}
                    >
                      <span className="exam-subject-tab-name">{subj}</span>
                      <span className="exam-subject-tab-count">
                        {stats?.obtained || 0}/{stats?.max || 0}
                      </span>
                    </button>
                  );
                })}
              </div>
              {isStudentOnlineExam && (
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {testPaperURL && testPaperURL !== "" && (
                    <button
                      onClick={handleOpenTestPaper}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        fontWeight: 600,
                        border: "1px solid #cbd5e1",
                        background: "#f1f5f9",
                        color: "#334155",
                        cursor: "pointer",
                      }}
                    >
                      <i className="fa-regular fa-eye" /> View Test Paper
                    </button>
                  )}
                  {answerPaperURL && answerPaperURL !== "" && (
                    <button
                      onClick={handleOpenAnswerPaper}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        fontWeight: 600,
                        border: "1px solid #cbd5e1",
                        background: "#f1f5f9",
                        color: "#334155",
                        cursor: "pointer",
                      }}
                    >
                      <i className="fa-regular fa-eye" /> View Answer Paper
                    </button>
                  )}
                  {issuedCertificate && (
                    <button
                      onClick={handleDownloadCertificate}
                      disabled={isDownloadingCert}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        fontWeight: 600,
                        border: "1px solid var(--color-1, #f09a21)",
                        background: "var(--color-1, #f09a21)",
                        color: "#ffffff",
                        cursor: "pointer",
                      }}
                      title="Download your completion certificate"
                    >
                      <i
                        className={`fa-solid ${isDownloadingCert ? "fa-spinner fa-spin" : "fa-award"}`}
                      />
                      Download Certificate
                    </button>
                  )}
                </div>
              )}
            </div>
            {activeSubject &&
              Object.keys(
                subjectStructure.subjectMap[effectiveSubject]?.sections || {},
              ).length > 0 && (
                <div
                  className="results-section-pills"
                  style={{
                    padding: "0 20px 12px 20px",
                    display: "flex",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                  }}
                >
                  {Object.keys(
                    subjectStructure.subjectMap[effectiveSubject].sections,
                  )
                    .sort(
                      (a, b) =>
                        subjectStructure.subjectMap[effectiveSubject].sections[
                          a
                        ].order -
                        subjectStructure.subjectMap[effectiveSubject].sections[
                          b
                        ].order,
                    )
                    .map((sec) => {
                      const secData =
                        subjectStructure.subjectMap[effectiveSubject].sections[
                          sec
                        ];
                      let secObtained = 0;
                      let secMax = 0;
                      secData.questions.forEach((q) => {
                        secObtained += q.obtainedScore || 0;
                        secMax += q.maxScore || 0;
                      });
                      return (
                        <button
                          key={sec}
                          className={`results-section-pill ${effectiveSection === sec ? "active" : ""}`}
                          onClick={() => {
                            setCurrentOEIndex(0);
                            setActiveSection(sec);
                          }}
                          style={{
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontSize: "0.8rem",
                            border: "1px solid #e2e8f0",
                            background:
                              effectiveSection === sec ? "#e0e7ff" : "#f8fafc",
                            color:
                              effectiveSection === sec ? "#3730a3" : "#475569",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span>{sec}</span>
                          <span style={{ fontSize: "0.75rem", opacity: 0.8 }}>
                            {secObtained}/{secMax}
                          </span>
                        </button>
                      );
                    })}
                </div>
              )}
          </div>
        </div>
      )}
      <motion.div
        className={`submitted-assessment ${
          isStudentOnlineExam || activeTab === "normal" || activeTab === "annotation"
            ? "results-exam-wrapper"
            : ""
        }`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        style={{ marginTop: "0", paddingTop: "0" }}
      >
        {!isStudentOnlineExam &&
          !testInfo.isPracticeTest &&
          activeTab === "table" && (
            <motion.div className="upper">
              <motion.div
                className="stats-container"
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div className="stats-box">
                  {[
                    {
                      title: "View Answer Paper",
                      onClick: handleOpenAnswerPaper,
                      icon: "/imgs/eye_icon.svg",
                    },
                    {
                      title: "Total Score",
                      value: `${totalScore.obtained}/${totalScore.max} (${((totalScore.obtained / totalScore.max) * 100).toFixed(1)}%)`,
                    },
                    {
                      title: "View Question Paper",
                      onClick: handleOpenTestPaper,
                      icon: "/imgs/eye_icon.svg",
                    },
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      className={`stat-card`}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 17,
                      }}
                      onClick={stat.onClick}
                    >
                      <h3>{stat.title}</h3>
                      <p>{stat.value}</p>
                      {stat.icon && (
                        <img
                          src={stat.icon}
                          alt="icon"
                          className="stat-img"
                          width={50}
                        />
                      )}
                      <div className="card-blur"></div>
                    </motion.div>
                  ))}
                </motion.div>
                <motion.div className="stats-box">
                  {[
                    {
                      title: "Performance Report",
                      onClick: handleViewSummary,
                      icon: "/imgs/eye_icon.svg",
                    },
                    {
                      title: "Export as CSV",
                      onClick: downloadCSV,
                      icon: "/imgs/download_icon.svg",
                    },
                    {
                      title: "Print Scores Report",
                      onClick: handlePrintReport,
                      icon: "/imgs/print_icon.svg",
                    },
                    ...(issuedCertificate
                      ? [
                          {
                            title: isDownloadingCert
                              ? "Downloading..."
                              : "Certificate",
                            value: "Download PNG",
                            onClick: handleDownloadCertificate,
                            customIcon: "fa-solid fa-award",
                            customColor: "var(--color-1, #f09a21)",
                          },
                        ]
                      : []),
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      className={`stat-card ${stat.badge ? "stat-card--violations" : ""}`}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 17,
                      }}
                      onClick={stat.onClick}
                    >
                      <h3>{stat.title}</h3>
                      <p>{stat.value}</p>
                      {stat.icon && (
                        <img
                          src={stat.icon}
                          alt="icon"
                          className="stat-img"
                          width={50}
                        />
                      )}
                      {stat.customIcon && (
                        <i
                          className={`${stat.customIcon} stat-img`}
                          style={{
                            fontSize: "2rem",
                            color: stat.customColor || "#ef4444",
                            opacity: stat.customColor ? 0.85 : 0.25,
                          }}
                        />
                      )}
                      {stat.badge && (
                        <span className="stat-card-badge">{stat.badge}</span>
                      )}
                      <div className="card-blur"></div>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>

              {/* Subject / Section Sidebar */}
              {hasSubjects && (
                <motion.div
                  className="results-subject-cards-container"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="results-subject-cards">
                    {subjectStructure.subjects.map((subj, idx) => {
                      const stats = subjectStats[subj];
                      const percentage =
                        stats?.max > 0 ? (stats.obtained / stats.max) * 100 : 0;
                      const isActive = effectiveSubject === subj;

                      return (
                        <motion.div
                          key={subj}
                          className={`results-subject-card ${isActive ? "active" : ""}`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.97 }}
                          transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 17,
                          }}
                          onClick={() => {
                            setCurrentOEIndex(0);
                            if (activeSubject === subj) {
                              setActiveSubject("");
                              setActiveSection("");
                            } else {
                              setActiveSubject(subj);
                              const secMap =
                                subjectStructure.subjectMap[subj]?.sections ||
                                {};
                              const sections = Object.keys(secMap).sort(
                                (a, b) => secMap[a].order - secMap[b].order,
                              );
                              setActiveSection(sections[0] || "");
                            }
                          }}
                        >
                          <div className="results-subject-card-name">
                            {subj}
                          </div>
                          <div className="results-subject-card-score">
                            <span className="results-subject-card-obtained">
                              {stats?.obtained || 0}
                            </span>
                            <span className="results-subject-card-separator">
                              /
                            </span>
                            <span className="results-subject-card-max">
                              {stats?.max || 0}
                            </span>
                          </div>

                          <div className="results-subject-card-counts">
                            <span className="results-subject-card-count-correct">
                              Correct: {stats?.correctCount || 0}
                            </span>
                            <span className="results-subject-card-count-incorrect">
                              Incorrect: {stats?.incorrectCount || 0}
                            </span>
                          </div>

                          <div className="results-subject-card-progress-track">
                            <motion.div
                              className="results-subject-card-progress-fill"
                              initial={{ width: 0 }}
                              animate={{ width: `${percentage}%` }}
                              transition={{
                                duration: 0.6,
                                ease: "easeOut",
                                delay: idx * 0.1,
                              }}
                            />
                          </div>
                          <div className="results-subject-card-percentage">
                            {percentage.toFixed(0)}%
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Section Pills */}
                  {effectiveSubject &&
                    subjectStructure.subjectMap[effectiveSubject] &&
                    Object.keys(
                      subjectStructure.subjectMap[effectiveSubject].sections,
                    ).length > 1 && (
                      <div className="results-section-pills">
                        {Object.keys(
                          subjectStructure.subjectMap[effectiveSubject]
                            .sections,
                        )
                          .sort(
                            (a, b) =>
                              subjectStructure.subjectMap[effectiveSubject]
                                .sections[a].order -
                              subjectStructure.subjectMap[effectiveSubject]
                                .sections[b].order,
                          )
                          .map((sec) => {
                            const secData =
                              subjectStructure.subjectMap[effectiveSubject]
                                .sections[sec];
                            let secObtained = 0;
                            let secMax = 0;
                            secData.questions.forEach((q) => {
                              secObtained += q.obtainedScore || 0;
                              secMax += q.maxScore || 0;
                            });

                            return (
                              <motion.button
                                key={sec}
                                type="button"
                                className={`results-section-pill ${effectiveSection === sec ? "active" : ""}`}
                                onClick={() => {
                                  setCurrentOEIndex(0);
                                  setActiveSection(sec);
                                }}
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                              >
                                <span className="results-section-pill-name">
                                  {sec}
                                </span>
                                <span className="results-section-pill-score">
                                  {secObtained}/{secMax}
                                </span>
                              </motion.button>
                            );
                          })}
                      </div>
                    )}
                </motion.div>
              )}
            </motion.div>
          )}

        {!isStudentOnlineExam && activeTab !== "annotation" && (
          <div className="tab-bar">
            <button
              className={`tab-button ${activeTab === "table" ? "active" : ""}`}
              onClick={() => setActiveTab("table")}
              style={{
                backgroundColor: activeTab === "table" ? "#4a90e2" : "#f5f5f5",
                color: activeTab === "table" ? "white" : "#333",
                transform: activeTab === "table" ? "scale(1.05)" : "scale(1)",
                transition: "all 0.3s ease",
              }}
            >
              <i className="fa-solid fa-table"></i>
            </button>
            <button
              className={`tab-button ${activeTab === "normal" ? "active" : ""}`}
              onClick={() => {
                setCurrentOEIndex(0);
                setActiveTab("normal");
              }}
              style={{
                backgroundColor: activeTab === "normal" ? "#4a90e2" : "#f5f5f5",
                color: activeTab === "normal" ? "white" : "#333",
                transform: activeTab === "normal" ? "scale(1.05)" : "scale(1)",
                transition: "all 0.3s ease",
              }}
            >
              <i className="fa-solid fa-list"></i>
            </button>
          </div>
        )}

        {(activeTab === "normal" || isStudentOnlineExam) &&
          (() => {
            const questions = filteredResult;

            if (!isOnlineExamination) {
              return (
                <div className="questions-container">
                  {(() => {
                    // When a specific subject is active and it has sections, group by section
                    if (
                      hasSubjects &&
                      effectiveSubject &&
                      subjectStructure.subjectMap[effectiveSubject]
                    ) {
                      const secMap =
                        subjectStructure.subjectMap[effectiveSubject].sections;
                      const sections = Object.keys(secMap).sort(
                        (a, b) => secMap[a].order - secMap[b].order,
                      );
                      // Also get questions with this subject but no sectionNumber
                      const unsectionedQuestions = filteredResult.filter(
                        (q) =>
                          q.subject === effectiveSubject && !q.sectionNumber,
                      );

                      return (
                        <>
                          {sections
                            .filter(
                              (sec) =>
                                !effectiveSection || sec === effectiveSection,
                            )
                            .map((sec) => (
                              <React.Fragment key={sec}>
                                <div className="results-section-header">
                                  <span className="results-section-header-label">
                                    <i
                                      className="fa-solid fa-layer-group"
                                      style={{
                                        marginRight: "0.4rem",
                                        fontSize: "0.65rem",
                                      }}
                                    ></i>
                                    {sec}
                                  </span>
                                  <div className="results-section-header-line"></div>
                                </div>
                                {secMap[sec].questions.map(
                                  (question, index) => (
                                    <motion.div
                                      key={question._id}
                                      className="question-card"
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: index * 0.05 }}
                                    >
                                      {renderQuestionCard(question)}
                                    </motion.div>
                                  ),
                                )}
                              </React.Fragment>
                            ))}
                          {unsectionedQuestions.map((question, index) => (
                            <motion.div
                              key={question._id}
                              className="question-card"
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              {renderQuestionCard(question)}
                            </motion.div>
                          ))}
                        </>
                      );
                    }

                    // Default: flat list (All tab or no subjects)
                    return filteredResult.map((question, index) => (
                      <motion.div
                        key={question._id}
                        className="question-card"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        {renderQuestionCard(question)}
                      </motion.div>
                    ));
                  })()}
                </div>
              );
            }

            const currentQ = questions[currentOEIndex];
            if (!currentQ || questions.length === 0)
              return (
                <div className="exam-empty-state">
                  <i className="fa-solid fa-circle-exclamation" />
                  <p>No questions found.</p>
                </div>
              );

            const studentAns = (currentQ.studentAnswer || "")
              .trim()
              .toUpperCase();
            const qType = (currentQ.type || "MCQ").toUpperCase();
            const correctAns =
              qType === "FILL-IN-THE-BLANKS" || qType === "NUMERICAL"
                ? (currentQ.correctAnswer || "").trim().toUpperCase()
                : currentQ.correctOptions && currentQ.correctOptions.length > 0
                  ? currentQ.correctOptions[0].trim().toUpperCase()
                  : "";
            const isCorrect = studentAns !== "" && studentAns === correctAns;
            const isUnanswered = !studentAns;

            let cleanedQuestionText = currentQ.questionText || "";
            if (qType === "MCQ") {
              // Only strip inline options if we detect at least A + B in sequence
              const stripped = cleanedQuestionText
                .replace(
                  /(?:\n|<br\s*\/?>)\s*(?:A[.)]|\(A\))\s+.+(?:\n|<br\s*\/?>)\s*(?:B[.)]|\(B\))\s+[\s\S]*$/i,
                  "",
                )
                .trim();
              if (stripped) {
                cleanedQuestionText = stripped;
              }
            }

            const typeMeta =
              qType === "NUMERICAL"
                ? {
                    label: "Numerical",
                    icon: "fa-calculator",
                    bg: "#fef3c7",
                    color: "#92400e",
                  }
                : qType === "FILL-IN-THE-BLANKS"
                  ? {
                      label: "Fill-in-the-Blank",
                      icon: "fa-pencil",
                      bg: "#dcfce7",
                      color: "#166534",
                    }
                  : {
                      label: "MCQ",
                      icon: "fa-list-ul",
                      bg: "#e0e7ff",
                      color: "#3730a3",
                    };

            return (
              <>
                <div
                  className={`exam-layout ${hasSubjects ? "with-subject-nav" : ""}`}
                >
                  <main className="exam-main-panel">
                    <div className="exam-q-topbar">
                      <div className="exam-q-number-badge">
                        <span className="exam-q-num">
                          Q{currentQ.questionNumber || currentOEIndex + 1}
                        </span>
                        <span className="exam-q-of">
                          of {scoreData.result.length}
                        </span>
                        {currentQ.subject && (
                          <span
                            className="exam-q-section-tag"
                            style={{
                              background: "#e0e7ff",
                              color: "#3730a3",
                              marginLeft: "0.5rem",
                            }}
                          >
                            {currentQ.subject}
                          </span>
                        )}
                        {currentQ.sectionNumber && (
                          <span
                            className="exam-q-section-tag"
                            style={{
                              background: "#fef3c7",
                              color: "#92400e",
                              marginLeft: "0.5rem",
                            }}
                          >
                            {currentQ.sectionNumber}
                          </span>
                        )}
                      </div>
                      <div className="exam-q-topbar-right">
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            padding: "3px 10px",
                            borderRadius: "10px",
                            background: typeMeta.bg,
                            color: typeMeta.color,
                            letterSpacing: "0.02em",
                          }}
                        >
                          <i
                            className={`fa-solid ${typeMeta.icon}`}
                            style={{ fontSize: "0.65rem" }}
                          />
                          {typeMeta.label}
                        </span>
                        <button
                          type="button"
                          className="btn-info-time"
                          onClick={() => handleOpenInfo(currentQ)}
                          title="View Time Taken & Analytics"
                        >
                          <i className="fa-regular fa-clock" />
                          <span>View Time</span>
                        </button>
                        <span
                          className={`oe-score-badge ${isCorrect ? "correct" : isUnanswered ? "unanswered" : "incorrect"}`}
                        >
                          {currentQ.obtainedScore}/{currentQ.maxScore}
                        </span>
                        <span
                          className={`oe-status-chip ${isCorrect ? "correct" : isUnanswered ? "unanswered" : "incorrect"}`}
                        >
                          <i
                            className={`fa-solid ${isCorrect ? "fa-circle-check" : isUnanswered ? "fa-minus" : "fa-circle-xmark"}`}
                          />
                          {isCorrect
                            ? "Correct"
                            : isUnanswered
                              ? "Unanswered"
                              : "Incorrect"}
                        </span>
                        {(role === "Instructor" || role === "Admin") && (
                          <button
                            className="btn-edit"
                            onClick={() => handleEditClick(currentQ)}
                            title="Edit"
                            style={{
                              display: "inline-flex",
                              padding: "0.4rem",
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                              color: "#64748b",
                            }}
                          >
                            <i
                              className="fa-solid fa-pen-to-square"
                              style={{ fontSize: "1.2rem" }}
                            />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="exam-q-content-wrapper">
                      <div className="exam-q-content exam-q-revamped-layout">
                        <div className="exam-q-stacked-body">
                          {/* 1. Question Text */}
                          <div className="exam-q-text">
                            <LazyMarkdown>
                              {formatLatexText(cleanedQuestionText)}
                            </LazyMarkdown>
                          </div>

                          {/* 2. Diagram (if available) */}
                          {currentQ.diagramFileURL && (
                            <div className="exam-q-diagram">
                              <div
                                className="exam-q-diagram-img-wrap"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setLightboxUrl(currentQ.diagramFileURL);
                                }}
                                title="Click to enlarge"
                              >
                                <img
                                  src={currentQ.diagramFileURL}
                                  alt={`Diagram for Q${currentOEIndex + 1}`}
                                  draggable={false}
                                />
                              </div>
                              <span className="exam-q-diagram-hint">
                                <i className="fa-solid fa-magnifying-glass" />{" "}
                                Click to enlarge
                              </span>
                            </div>
                          )}

                          {/* 3. Options (A B on Row 1, C D on Row 2) */}
                          {qType === "MCQ" &&
                            currentQ.options &&
                            Array.isArray(currentQ.options) && (
                              <div className="exam-q-options-grid">
                                {currentQ.options
                                  .filter(Boolean)
                                  .map((opt, optIndex) => {
                                    const optKey = (
                                      opt && typeof opt === "object"
                                        ? opt.key
                                        : ["A", "B", "C", "D", "E", "F"][
                                            optIndex
                                          ] || String(optIndex + 1)
                                    )
                                      .trim()
                                      .toUpperCase();
                                    const optText =
                                      opt && typeof opt === "object"
                                        ? opt.text
                                        : opt;
                                    if (!optText) return null;
                                    const displayLetter =
                                      ["A", "B", "C", "D", "E", "F"][
                                        optIndex
                                      ] || String(optIndex + 1);
                                    const isStudentPick = studentAns === optKey;
                                    const isCorrectOption =
                                      correctAns === optKey;

                                    let optionClass = "exam-q-option-compact";
                                    let optionStyle = {
                                      cursor: "default",
                                      opacity: 1,
                                    };
                                    let letterStyle = {};
                                    let textStyle = {};

                                    if (isStudentPick && isCorrectOption) {
                                      optionClass += " selected correct";
                                      optionStyle.border = "2px solid #16a34a";
                                      optionStyle.background = "#f0fdf4";
                                      letterStyle.background = "#16a34a";
                                      letterStyle.color = "#fff";
                                      textStyle.color = "#15803d";
                                      textStyle.fontWeight = "600";
                                    } else if (
                                      isStudentPick &&
                                      !isCorrectOption
                                    ) {
                                      optionClass += " selected incorrect";
                                      optionStyle.border = "2px solid #dc2626";
                                      optionStyle.background = "#fef2f2";
                                      letterStyle.background = "#dc2626";
                                      letterStyle.color = "#fff";
                                      textStyle.color = "#b91c1c";
                                      textStyle.fontWeight = "600";
                                    } else if (
                                      isCorrectOption &&
                                      !isUnanswered
                                    ) {
                                      optionClass += " correct-target";
                                      optionStyle.border = "2px dashed #16a34a";
                                      optionStyle.background = "#f0fdf4";
                                      letterStyle.background = "#16a34a";
                                      letterStyle.color = "#fff";
                                      textStyle.color = "#15803d";
                                      textStyle.fontWeight = "600";
                                    }

                                    return (
                                      <div
                                        key={optKey}
                                        className={optionClass}
                                        style={optionStyle}
                                      >
                                        <span
                                          className="exam-q-option-letter"
                                          style={letterStyle}
                                        >
                                          {displayLetter}
                                        </span>
                                        <span
                                          className="exam-q-option-text"
                                          style={textStyle}
                                        >
                                          <LazyMarkdown>
                                            {formatLatexText(optText)}
                                          </LazyMarkdown>
                                        </span>
                                        {isStudentPick && (
                                          <span
                                            className="oe-option-indicator"
                                            style={{
                                              marginLeft: "auto",
                                              display: "inline-flex",
                                              width: "22px",
                                              height: "22px",
                                              borderRadius: "50%",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              color: "#fff",
                                              background: isCorrectOption
                                                ? "#16a34a"
                                                : "#dc2626",
                                              flexShrink: 0,
                                            }}
                                          >
                                            <i
                                              className={`fa-solid ${isCorrectOption ? "fa-check" : "fa-xmark"}`}
                                              style={{ fontSize: "11px" }}
                                            />
                                          </span>
                                        )}
                                        {isCorrectOption &&
                                          !isStudentPick &&
                                          !isUnanswered && (
                                            <span
                                              className="oe-option-indicator correct"
                                              style={{
                                                marginLeft: "auto",
                                                display: "inline-flex",
                                                width: "22px",
                                                height: "22px",
                                                borderRadius: "50%",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#fff",
                                                background: "#16a34a",
                                                flexShrink: 0,
                                              }}
                                            >
                                              <i
                                                className="fa-solid fa-check"
                                                style={{ fontSize: "11px" }}
                                              />
                                            </span>
                                          )}
                                      </div>
                                    );
                                  })}
                              </div>
                            )}

                          {(qType === "NUMERICAL" ||
                            qType === "FILL-IN-THE-BLANKS") && (
                            <div
                              className="oe-answer-compare compact"
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                gap: "1rem",
                                width: "100%",
                              }}
                            >
                              <div
                                className={`oe-answer-box ${isUnanswered ? "unanswered" : isCorrect ? "correct" : "incorrect"}`}
                              >
                                <span className="oe-answer-label">
                                  Your Answer
                                </span>
                                <span className="oe-answer-value">
                                  {isUnanswered
                                    ? "Not Answered"
                                    : currentQ.studentAnswer}
                                </span>
                              </div>
                              <div className="oe-answer-box correct">
                                <span className="oe-answer-label">
                                  Correct Answer
                                </span>
                                <span className="oe-answer-value">
                                  {currentQ.correctAnswer || "-"}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="exam-action-bar">
                      <div className="exam-action-left">
                        <button
                          type="button"
                          className="btn-view-solution-secondary"
                          onClick={() => {
                            setSolutionQuestion(currentQ);
                            setIsSolutionModalOpen(true);
                          }}
                        >
                          <i className="fa-solid fa-lightbulb" />
                          <span>View Solution</span>
                        </button>
                      </div>
                      <div className="exam-action-right">
                        <button
                          type="button"
                          className="exam-btn exam-btn-prev"
                          onClick={() =>
                            setCurrentOEIndex((prev) => Math.max(0, prev - 1))
                          }
                          disabled={currentOEIndex === 0}
                        >
                          <i className="fa-solid fa-chevron-left" />
                          Previous
                        </button>
                        <button
                          type="button"
                          className="exam-btn exam-btn-save"
                          onClick={() =>
                            setCurrentOEIndex((prev) =>
                              Math.min(questions.length - 1, prev + 1),
                            )
                          }
                          disabled={currentOEIndex >= questions.length - 1}
                        >
                          Next
                          <i className="fa-solid fa-chevron-right" />
                        </button>
                      </div>
                    </div>
                  </main>

                  <aside
                    className={`exam-sidebar ${showSidebar ? "open" : ""}`}
                  >
                    <div className="exam-palette-section">
                      <div className="exam-palette-header">
                        <div className="exam-palette-title-wrap">
                          <i className="fa-solid fa-table-cells" />
                          <h4 className="exam-palette-title">
                            Question Palette
                          </h4>
                        </div>
                        <button
                          className="exam-sidebar-close"
                          onClick={() => setShowSidebar(false)}
                          type="button"
                          aria-label="Close question palette"
                        >
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                      <div className="exam-palette-body">
                        <div className="exam-palette-grid">
                          {questions.map((q, idx) => {
                            const status = paletteData.statuses[idx] || {};
                            let bg = status.isUnanswered
                              ? "#cbd5e1"
                              : status.isCorrect
                                ? "#22c55e"
                                : "#ef4444";
                            let color = status.isUnanswered
                              ? "#475569"
                              : "#fff";
                            let border =
                              idx === currentOEIndex
                                ? "3px solid #0f172a"
                                : "none";
                            let transform =
                              idx === currentOEIndex
                                ? "scale(1.08)"
                                : "scale(1)";
                            let zIndex = idx === currentOEIndex ? 2 : 1;

                            return (
                              <button
                                key={q._id}
                                className="exam-palette-btn"
                                style={{
                                  background: bg,
                                  color: color,
                                  border: border,
                                  transform: transform,
                                  zIndex: zIndex,
                                  transition: "all 0.2s",
                                }}
                                onClick={() => {
                                  setCurrentOEIndex(idx);
                                  setShowSidebar(false);
                                }}
                              >
                                {q.questionNumber || idx + 1}
                              </button>
                            );
                          })}
                        </div>
                        <div className="exam-palette-legend">
                          <div className="legend-item">
                            <span
                              className="legend-dot"
                              style={{ background: "#22c55e" }}
                            />{" "}
                            Correct
                          </div>
                          <div className="legend-item">
                            <span
                              className="legend-dot"
                              style={{ background: "#ef4444" }}
                            />{" "}
                            Incorrect
                          </div>
                          <div className="legend-item">
                            <span
                              className="legend-dot"
                              style={{ background: "#cbd5e1" }}
                            />{" "}
                            Unanswered
                          </div>
                        </div>

                        <div className="oe-palette-stats">
                          <div className="oe-stat correct">
                            <strong>{paletteData.counts.correct}</strong>
                            <span>Correct</span>
                          </div>
                          <div className="oe-stat incorrect">
                            <strong>{paletteData.counts.incorrect}</strong>
                            <span>Incorrect</span>
                          </div>
                          <div className="oe-stat unanswered">
                            <strong>{paletteData.counts.unanswered}</strong>
                            <span>Skipped</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </aside>
                </div>

                {/* Mobile Sidebar Toggle Button */}
                {!showSidebar && (
                  <button
                    type="button"
                    className="exam-sidebar-toggle"
                    onClick={() => setShowSidebar(true)}
                    title="View Question Palette"
                    aria-label="View Question Palette"
                  >
                    <i className="fa-solid fa-table-cells" />
                    <span>
                      Q{currentOEIndex + 1}/{questions.length}
                    </span>
                  </button>
                )}

                {/* Mobile Backdrop */}
                {showSidebar && (
                  <div
                    className="exam-sidebar-backdrop"
                    onClick={() => setShowSidebar(false)}
                  />
                )}
              </>
            );
          })()}

        {activeTab === "table" && !isStudentOnlineExam && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {role === "Student" && (
              <div className="action-buttons">
                {isRegradeRequestSubmitted && (
                  <motion.button
                    className="publish-results-button"
                    onClick={() =>
                      setIsViewingRegradeRequests(!isViewingRegradeRequests)
                    }
                  >
                    <i className="fa-solid"></i>{" "}
                    {isViewingRegradeRequests
                      ? "Hide Regrade Requests"
                      : "View Regrade Requests"}
                  </motion.button>
                )}
                {!isRegradeRequestSubmitted &&
                  pendingRegradeRequests.length > 0 && (
                    <motion.button
                      className="publish-results-button"
                      onClick={handleSubmitRegradeRequest}
                    >
                      <i className="fa-solid"></i> Submit Regrade Request
                    </motion.button>
                  )}
              </div>
            )}

            <Table
              data={
                role === "Student" && isViewingRegradeRequests
                  ? submittedRegradeRequests
                  : filteredResult.filter((item) => {
                      if (
                        (role === "Instructor" || role === "Admin") &&
                        manualReview
                      ) {
                        return item.isManualReview === true;
                      }
                      return true;
                    })
              }
              columns={columns}
              caption={
                role === "Student" && isViewingRegradeRequests
                  ? "Regrade Requests"
                  : `Assessment Results for ${studentInfo.name}`
              }
              searchActions={
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", position: "relative" }}>
                  {(role === "Instructor" || role === "Admin") && proctoringLog ? (
                    <button
                      onClick={() => setShowProctoringModal(true)}
                      className="publish-results-button"
                      style={{
                        background: "#ef4444",
                        border: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        position: "relative",
                      }}
                    >
                      <i className="fa-solid fa-shield-halved"></i> Proctoring Log
                      <span
                        style={{
                          background: "#fff",
                          color: "#ef4444",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          borderRadius: "50%",
                          padding: "2px 6px",
                          marginLeft: "4px",
                        }}
                      >
                        {proctoringLog.violations.length}
                      </span>
                    </button>
                  ) : null}

                  {!isStudentOnlineExam && (
                    <div style={{ position: "relative" }} ref={annotationsDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setShowAnnotationsMenu((prev) => !prev)}
                        className="annotations-action-trigger-btn"
                        style={{
                          background: "linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: "8px",
                          padding: "8px 16px",
                          fontSize: "0.88rem",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          cursor: "pointer",
                          boxShadow: "0 2px 8px rgba(79, 70, 229, 0.28)",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <i className="fa-solid fa-pen-nib"></i>
                        <span>Annotations</span>
                        <i
                          className={`fa-solid fa-chevron-${showAnnotationsMenu ? "up" : "down"}`}
                          style={{ fontSize: "0.72rem", opacity: 0.85, marginLeft: "2px" }}
                        ></i>
                      </button>

                      {showAnnotationsMenu && (
                        <div
                          className="annotations-menu-popover"
                          style={{
                            position: "absolute",
                            top: "calc(100% + 8px)",
                            right: 0,
                            width: "290px",
                            background: "#ffffff",
                            borderRadius: "12px",
                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08)",
                            border: "1px solid #e2e8f0",
                            padding: "8px",
                            zIndex: 1050,
                            animation: "popoverFadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "0.74rem",
                              fontWeight: "700",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              color: "#64748b",
                              padding: "6px 10px 6px 10px",
                              borderBottom: "1px solid #f1f5f9",
                              marginBottom: "4px",
                            }}
                          >
                            Paper Annotations
                          </div>

                          <button
                            type="button"
                            className="annotation-menu-item"
                            disabled={isViewingAnnotatedPdf}
                            onClick={handleViewAnnotatedPdf}
                            style={{
                              width: "100%",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "12px",
                              padding: "10px",
                              background: "transparent",
                              border: "none",
                              borderRadius: "8px",
                              cursor: isViewingAnnotatedPdf ? "not-allowed" : "pointer",
                              textAlign: "left",
                              transition: "background 0.15s ease",
                            }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "8px",
                                background: "#ecfdf5",
                                color: "#059669",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                fontSize: "1.05rem",
                              }}
                            >
                              <i
                                className={`fa-solid ${
                                  isViewingAnnotatedPdf
                                    ? "fa-circle-notch fa-spin"
                                    : "fa-file-lines"
                                }`}
                              ></i>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: "0.88rem",
                                  fontWeight: "600",
                                  color: "#0f172a",
                                }}
                              >
                                {isViewingAnnotatedPdf ? "Preparing PDF..." : "View Annotated PDF"}
                              </div>
                              <div
                                style={{
                                  fontSize: "0.76rem",
                                  color: "#64748b",
                                  marginTop: "2px",
                                  lineHeight: 1.35,
                                }}
                              >
                                Preview evaluated paper with all markings & scores
                              </div>
                            </div>
                          </button>

                          <button
                            type="button"
                            className="annotation-menu-item"
                            onClick={() => {
                              setShowAnnotationsMenu(false);
                              setActiveTab("annotation");
                            }}
                            style={{
                              width: "100%",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "12px",
                              padding: "10px",
                              background: "transparent",
                              border: "none",
                              borderRadius: "8px",
                              cursor: "pointer",
                              textAlign: "left",
                              transition: "background 0.15s ease",
                              marginTop: "4px",
                            }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "8px",
                                background: "#eff6ff",
                                color: "#2563eb",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                fontSize: "1.05rem",
                              }}
                            >
                              <i className="fa-solid fa-pen-to-square"></i>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: "0.88rem",
                                  fontWeight: "600",
                                  color: "#0f172a",
                                }}
                              >
                                Edit Annotations
                              </div>
                              <div
                                style={{
                                  fontSize: "0.76rem",
                                  color: "#64748b",
                                  marginTop: "2px",
                                  lineHeight: 1.35,
                                }}
                              >
                                Open visual red pen canvas to add, drag or resize marks
                              </div>
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              }
            />
          </motion.div>
        )}

        {/* INSTRUCTOR MODALS */}
        {(role === "Instructor" || role === "Admin") && (
          <>
            <Modal isOpen={isModalOpen} onClose={closeModal}>
              <div className="edit-score-modal">
                <h2>Edit Question Score and Feedback</h2>
                {currentEditingQuestion && (
                  <form onSubmit={handleSubmitEdit}>
                    <div className="form-group">
                      <label htmlFor="questionNumber">
                        <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                        Question Number:
                      </label>
                      <input
                        type="text"
                        id="questionNumber"
                        name="questionNumber"
                        value={editForm.questionNumber}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                        Question: {currentEditingQuestion.questionText}
                      </label>
                    </div>
                    <div className="form-group">
                      <label htmlFor="studentAnswer">
                        <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                        Student's Answer:
                      </label>
                      <textarea
                        id="studentAnswer"
                        name="studentAnswer"
                        value={editForm.studentAnswer}
                        onChange={handleFormChange}
                        rows="4"
                        required
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label htmlFor="obtainedScore">
                        <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                        Obtained Score:
                      </label>
                      <input
                        type="number"
                        id="obtainedScore"
                        name="obtainedScore"
                        value={editForm.obtainedScore}
                        onChange={handleFormChange}
                        min="0"
                        max={currentEditingQuestion.maxScore}
                        step="0.25"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="maxScore">
                        <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                        Max Score:
                      </label>
                      <input
                        type="number"
                        id="maxScore"
                        name="maxScore"
                        value={editForm.maxScore}
                        onChange={handleFormChange}
                        min="0"
                        step="0.25"
                        required
                      />
                    </div>
                    {(parseFloat(editForm.obtainedScore) !==
                      currentEditingQuestion.obtainedScore ||
                      currentEditingQuestion.obtainedScoreLogs) && (
                      <div className="form-group">
                        <label htmlFor="scoreChangeReason">
                          {parseFloat(editForm.obtainedScore) !==
                          currentEditingQuestion.obtainedScore ? (
                            <i className="fa-solid fa-asterisk requiredIcon"></i>
                          ) : (
                            <i className="fa-solid fa-clock-rotate-left"></i>
                          )}{" "}
                          Score Change Reason:
                        </label>
                        <textarea
                          id="scoreChangeReason"
                          name="scoreChangeReason"
                          value={editForm.scoreChangeReason}
                          onChange={handleFormChange}
                          rows="4"
                          required={
                            parseFloat(editForm.obtainedScore) !==
                            currentEditingQuestion.obtainedScore
                          }
                          placeholder="Reason for score change"
                        ></textarea>
                        {parseFloat(editForm.obtainedScore) !==
                          currentEditingQuestion.obtainedScore && (
                          <div
                            style={{
                              marginTop: "8px",
                              color: "#6c757d",
                              fontSize: "0.9em",
                            }}
                          >
                            <p style={{ margin: 0 }}>
                              <i className="fa-solid fa-info-circle"></i>{" "}
                              {`Score will be updated from ${currentEditingQuestion.obtainedScore} to ${editForm.obtainedScore}`}
                            </p>
                          </div>
                        )}
                        {currentEditingQuestion.obtainedScoreLogs &&
                          parseFloat(editForm.obtainedScore) ===
                            currentEditingQuestion.obtainedScore && (
                            <div
                              style={{
                                marginTop: "8px",
                                color: "#6c757d",
                                fontSize: "0.9em",
                              }}
                            >
                              <p style={{ margin: 0 }}>
                                <i className="fa-solid fa-info-circle"></i>{" "}
                                {`Previous score was ${currentEditingQuestion.obtainedScoreLogs.previousObtainedScore}`}
                              </p>
                            </div>
                          )}
                      </div>
                    )}
                    <div className="form-group">
                      <label htmlFor="feedback">
                        Feedback{" "}
                        <span className="optional-text">(Optional)</span>
                      </label>
                      <textarea
                        id="feedback"
                        name="feedback"
                        value={editForm.feedback}
                        onChange={handleFormChange}
                        rows="4"
                      ></textarea>
                    </div>
                    {currentEditingQuestion.isManualReview && (
                      <div className="form-group">
                        <label className="form-checkbox">
                          <input
                            type="checkbox"
                            name="isManualReview"
                            checked={editForm.isManualReview}
                            onChange={handleFormChange}
                          />
                          <span className="checkbox-label">
                            Marked for Manual Review
                          </span>
                        </label>
                        <p className="form-helper">
                          Uncheck when you’ve reviewed and approved this score.
                        </p>
                      </div>
                    )}
                    <div className="form-actions">
                      <button
                        type="submit"
                        className="btn-submit"
                        disabled={!isSubmitEnabled || updateLoading}
                      >
                        {updateLoading ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </Modal>

            <Modal
              isOpen={isCommentsModalOpen}
              onClose={() => setIsCommentsModalOpen(false)}
            >
              <h2>Edit Answer Paper Comments</h2>
              <form
                onSubmit={handleUpdateComments}
                className="special-instruction-form"
              >
                <div className="form-group">
                  <label>Comments:</label>
                  <textarea
                    name="answerPaperComments"
                    value={answerPaperComments}
                    onChange={(e) => setAnswerPaperComments(e.target.value)}
                    rows="4"
                  />
                </div>
                <button type="submit" className="btn-submit">
                  Update Comments
                </button>
              </form>
            </Modal>
          </>
        )}

        {activeTab === "annotation" && !isStudentOnlineExam && (
          <AnnotationStudio
            answerPaperId={answerPaperId}
            paperUrl={answerPaperURL}
            scoreData={scoreData}
            studentInfo={studentInfo}
            testInfo={testInfo}
            serverUrl={serverUrl}
            fetchWithAuth={fetchWithAuth}
            role={role}
            swalSuccess={swalSuccess}
            swalError={swalError}
            swalWarning={swalWarning}
            swalToast={swalToast}
            onScoreDataChange={setScoreData}
            onClose={() => setActiveTab("table")}
          />
        )}

        {/* COMMON MODAL FOR ALL ROLES */}
        <Modal
          isOpen={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
          maxWidth={"60%"}
          modalContentClass={"time-stats-modal"}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              paddingBottom: "16px",
              marginBottom: "20px",
              borderBottom: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: infoData.isOnlineExam
                  ? "linear-gradient(135deg, #6366f1, #8b5cf6)"
                  : "linear-gradient(135deg, #3b82f6, #06b6d4)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.4rem",
                boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)",
              }}
            >
              <i
                className={
                  infoData.isOnlineExam
                    ? "fa-solid fa-stopwatch"
                    : "fa-solid fa-circle-info"
                }
              ></i>
            </div>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "1.35rem",
                  fontWeight: "800",
                  color: "#1e293b",
                  border: "none",
                  padding: 0,
                }}
              >
                {infoData.isOnlineExam
                  ? "Time Analytics & Insights"
                  : "Question Insights"}
              </h2>
              <p
                style={{
                  margin: "4px 0 0 0",
                  fontSize: "0.85rem",
                  color: "#64748b",
                }}
              >
                {infoData.isOnlineExam
                  ? "Comparative timing benchmarks across student attempts"
                  : "Reference metadata and detailed grading rationale"}
              </p>
            </div>
          </div>

          {infoData.isOnlineExam ? (
            <div className="time-analytics-container">
              {/* Card 1: Your Time */}
              <div className="analytics-card primary">
                <div className="analytics-card-left">
                  <div className="analytics-icon-badge primary">
                    <i className="fa-solid fa-user-clock"></i>
                  </div>
                  <div className="analytics-card-info">
                    <h4 className="analytics-card-title">Your Time</h4>
                    <span className="analytics-card-subtitle">
                      Time spent on your attempt for this question
                    </span>
                  </div>
                </div>
                <div className="analytics-card-right">
                  <span className="analytics-time-value primary">
                    {formatInfoTime(infoData.timeTaken)}
                  </span>
                </div>
              </div>

              {/* Card 2: Correct Average */}
              <div className="analytics-card success">
                <div className="analytics-card-left">
                  <div className="analytics-icon-badge success">
                    <i className="fa-solid fa-circle-check"></i>
                  </div>
                  <div className="analytics-card-info">
                    <h4 className="analytics-card-title">
                      Average Time (Correct Answers)
                    </h4>
                    <span className="analytics-card-subtitle">
                      Benchmark time of students who solved it correctly
                    </span>
                  </div>
                </div>
                <div className="analytics-card-right">
                  <span className="analytics-time-value success">
                    {formatInfoTime(infoData.avgCorrectTimeTaken)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                marginTop: "10px",
              }}
            >
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "14px",
                  padding: "18px 20px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    color: "#4f46e5",
                    fontWeight: "700",
                    fontSize: "0.95rem",
                    marginBottom: "10px",
                  }}
                >
                  <i
                    className="fa-solid fa-book-open"
                    style={{ fontSize: "1.1rem" }}
                  ></i>{" "}
                  Source
                </div>
                <p
                  style={{
                    margin: 0,
                    color: "#334155",
                    fontSize: "0.95rem",
                    lineHeight: "1.6",
                  }}
                >
                  {infoData.source || "NA"}
                </p>
              </div>
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "14px",
                  padding: "18px 20px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    color: "#059669",
                    fontWeight: "700",
                    fontSize: "0.95rem",
                    marginBottom: "10px",
                  }}
                >
                  <i
                    className="fa-solid fa-brain"
                    style={{ fontSize: "1.1rem" }}
                  ></i>{" "}
                  Reasoning
                </div>
                <p
                  style={{
                    margin: 0,
                    color: "#334155",
                    fontSize: "0.95rem",
                    lineHeight: "1.6",
                  }}
                >
                  {infoData.reasoning || "NA"}
                </p>
              </div>
            </div>
          )}
        </Modal>

        {/* SOLUTION POPUP MODAL */}
        <Modal
          isOpen={isSolutionModalOpen}
          onClose={() => setIsSolutionModalOpen(false)}
          maxWidth="680px"
        >
          {solutionQuestion &&
            (() => {
              const sType = (solutionQuestion.type || "MCQ").toUpperCase();
              const sStudentAns = (solutionQuestion.studentAnswer || "")
                .trim()
                .toUpperCase();
              const sCorrectAns =
                sType === "FILL-IN-THE-BLANKS" || sType === "NUMERICAL"
                  ? (solutionQuestion.correctAnswer || "").trim().toUpperCase()
                  : solutionQuestion.correctOptions &&
                      solutionQuestion.correctOptions.length > 0
                    ? solutionQuestion.correctOptions[0].trim().toUpperCase()
                    : "";
              const sIsCorrect =
                sStudentAns !== "" && sStudentAns === sCorrectAns;
              const sIsUnanswered = !sStudentAns;

              return (
                <div className="solution-modal-container">
                  <div className="solution-modal-header">
                    <div className="solution-modal-icon">
                      <i className="fa-solid fa-lightbulb"></i>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h2
                        style={{
                          margin: 0,
                          fontSize: "1.25rem",
                          fontWeight: "800",
                          color: "#1e293b",
                          border: "none",
                          padding: 0,
                        }}
                      >
                        Detailed Solution — Q
                        {solutionQuestion.questionNumber || currentOEIndex + 1}
                      </h2>
                      <p
                        style={{
                          margin: "3px 0 0 0",
                          fontSize: "0.82rem",
                          color: "#64748b",
                        }}
                      >
                        {solutionQuestion.subject
                          ? `${solutionQuestion.subject} • `
                          : ""}
                        {solutionQuestion.type || "MCQ"}
                      </p>
                    </div>
                    <div className="solution-modal-badge">
                      <span
                        style={{
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          padding: "4px 10px",
                          borderRadius: "12px",
                          background: sIsCorrect
                            ? "#dcfce7"
                            : sIsUnanswered
                              ? "#f1f5f9"
                              : "#fee2e2",
                          color: sIsCorrect
                            ? "#15803d"
                            : sIsUnanswered
                              ? "#475569"
                              : "#b91c1c",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <i
                          className={`fa-solid ${sIsCorrect ? "fa-circle-check" : sIsUnanswered ? "fa-minus" : "fa-circle-xmark"}`}
                        />
                        {sIsCorrect
                          ? "Correct"
                          : sIsUnanswered
                            ? "Unanswered"
                            : "Incorrect"}{" "}
                        ({solutionQuestion.obtainedScore}/
                        {solutionQuestion.maxScore})
                      </span>
                    </div>
                  </div>

                  {/* Solution Body */}
                  <div className="solution-modal-body">
                    <h4 className="solution-section-title">
                      <i
                        className="fa-solid fa-file-lines"
                        style={{ marginRight: "6px", color: "#6366f1" }}
                      />
                      Explanation &amp; Steps
                    </h4>
                    <div className="solution-markdown-content">
                      <LazyMarkdown>
                        {formatLatexText(
                          solutionQuestion.feedback ||
                            solutionQuestion.solution ||
                            "No detailed explanation provided for this question.",
                        )}
                      </LazyMarkdown>
                    </div>
                  </div>
                </div>
              );
            })()}
        </Modal>

        {/* STUDENT MODALS */}
        {role === "Student" && (
          <Modal
            isOpen={isRegradeCommentModalOpen}
            onClose={handleCloseRegradeCommentModal}
          >
            <div className="edit-score-modal">
              <h2>Request Regrade</h2>
              <form onSubmit={handleRegradeCommentSubmit}>
                <div className="form-group">
                  <label>
                    <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                    Question Number
                  </label>
                  <input
                    type="text"
                    value={currentRegradeRequest.questionNumber || ""}
                    disabled
                    placeholder="Question number"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                    Question
                  </label>
                  <textarea
                    value={currentRegradeRequest.questionText || ""}
                    disabled
                    rows={2}
                    placeholder="Question text"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                    Student's Answer
                  </label>
                  <textarea
                    value={currentRegradeRequest.studentAnswer || ""}
                    disabled
                    rows={2}
                    placeholder="Student's answer"
                  />
                </div>
                <div className="form-group">
                  <label>
                    Feedback <span className="optional-text">(Optional)</span>
                  </label>
                  <textarea
                    value={currentRegradeRequest.feedback || ""}
                    disabled
                    rows={2}
                    placeholder="Feedback from instructor"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="regradeComment">
                    <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                    Regrade Comment
                  </label>
                  <textarea
                    value={currentRegradeRequest.regradeComment || ""}
                    onChange={handleCurrentRegradeRequestChange}
                    name="regradeComment"
                    placeholder="Explain why you request a regrade (required)"
                    required
                    disabled={isRegradeRequestSubmitted}
                    rows={3}
                  />
                  <small className="helper-text">
                    Clearly explain why you believe your answer deserves a
                    regrade.
                  </small>
                </div>
                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={isSubmitting || isRegradeRequestSubmitted}
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </Modal>
        )}

        {/* COMMON SUMMARY MODAL */}
        <Modal
          isOpen={isSummaryModalOpen}
          onClose={handleCloseSummaryModal}
          maxWidth={role === "Student" ? "1200px" : "100%"}
        >
          {role === "Student" ? (
            scoreData.summary ? (
              <div className="summary-content">
                <div ref={summaryRef}>
                  <PerformanceReport
                    summary={{
                      studentName: studentInfo.name,
                      testName: testInfo.name,
                      classId: classId,
                      instructorName: classInfo.instructorName,
                      overallPerformance:
                        scoreData?.summary?.overallPerformance || "",
                      strengths: scoreData?.summary?.strengths || [],
                      weaknesses: scoreData?.summary?.weaknesses || [],
                      recommendations:
                        scoreData?.summary?.recommendations || [],
                      createdAt: scoreData?.summary?.createdAt || "",
                      updatedAt: scoreData?.summary?.updatedAt || "",
                    }}
                    isSummaryEditing={false}
                    onSaveSummary={false}
                    handleSaveSummary={() => {}}
                  />
                </div>
                <div className="modal-actions">
                  <button
                    className="btn-submit"
                    onClick={handlePrintSummary}
                    title="Print the performance report"
                  >
                    <i className="fa-solid fa-print"></i> Print
                  </button>
                </div>
              </div>
            ) : (
              <p>No summary available.</p>
            )
          ) : isSummaryGenerating ? (
            <div className="loading-container">
              <p style={{ marginLeft: "48%", marginBottom: "10px" }}>
                Loading ...
              </p>
              <Loader style={{ fontSize: "50px" }} />
            </div>
          ) : (
            <>
              {scoreData.summary ? (
                <div className="summary-content">
                  <div ref={summaryRef}>
                    <PerformanceReport
                      summary={{
                        studentName: studentInfo.name,
                        testName: testInfo.name,
                        classId: classId,
                        instructorName: classInfo.instructorName,
                        overallPerformance:
                          scoreData?.summary?.overallPerformance || "",
                        strengths: scoreData?.summary?.strengths || [],
                        weaknesses: scoreData?.summary?.weaknesses || [],
                        recommendations:
                          scoreData?.summary?.recommendations || [],
                        createdAt: scoreData?.summary?.createdAt || "",
                        updatedAt: scoreData?.summary?.updatedAt || "",
                      }}
                      isSummaryEditing={isSummaryEditing}
                      onSaveSummary={onSaveSummary}
                      handleSaveSummary={handleSaveSummary}
                    />
                  </div>
                  <div className="modal-actions">
                    {!isSummaryEditing ? (
                      <>
                        <button
                          className="btn-submit delete-btn-summary"
                          onClick={handleDeleteSummary}
                          title="Delete the performance report"
                          style={{ backgroundColor: "#ef4444" }}
                          disabled={isSummarySaving}
                        >
                          <i className="fa-solid fa-trash"></i>{" "}
                          {isSummarySaving ? "Deleting..." : "Delete"}
                        </button>
                        <button
                          className="btn-submit"
                          onClick={handlePrintSummary}
                          title="Print the performance report"
                        >
                          <i className="fa-solid fa-print"></i> Print
                        </button>
                        <button
                          className="btn-submit"
                          onClick={handleEditSummary}
                          title="Edit the performance report"
                        >
                          <i className="fa-solid fa-edit"></i> Edit
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn-submit"
                          onClick={handleCancelEditSummary}
                          title="Cancel editing"
                        >
                          <i className="fa-solid fa-xmark"></i> Cancel
                        </button>
                        <button
                          className="btn-submit"
                          onClick={onSaveSummaryClick}
                          title="Save your edit"
                          disabled={isSummarySaving}
                        >
                          <i className="fa-solid fa-floppy-disk"></i>{" "}
                          {!isSummarySaving ? "Save" : "Saving..."}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  className="no-summary-message"
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    backgroundColor: "#fff1f2",
                    borderRadius: "12px",
                    border: "2px dashed #fda4af",
                  }}
                >
                  <div
                    style={{
                      fontSize: "48px",
                      marginBottom: "16px",
                      color: "#e11d48",
                    }}
                  >
                    <i className="fa-solid fa-circle-exclamation"></i>
                  </div>
                  <h3
                    style={{
                      fontSize: "20px",
                      color: "#9f1239",
                      marginBottom: "8px",
                    }}
                  >
                    Performance Report Not Available
                  </h3>
                  <p
                    style={{
                      color: "#be123c",
                      fontSize: "16px",
                      lineHeight: "1.5",
                      maxWidth: "400px",
                      margin: "0 auto",
                    }}
                  >
                    The Performance Report is not yet generated for this
                    student. You can generate it by visiting the{" "}
                    <strong style={{ textDecoration: "underline" }}>
                      Submitted Assessments
                    </strong>{" "}
                    page.
                  </p>
                </div>
              )}
            </>
          )}
        </Modal>
      </motion.div>

      {/* ── Proctoring Log Modal ─────────────────────────────────── */}
      {showProctoringModal && proctoringLog && (
        <div
          className="proctoring-modal-overlay"
          onClick={() => setShowProctoringModal(false)}
        >
          <div
            className="proctoring-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="proctoring-modal-header">
              <div className="proctoring-modal-header-left">
                <div className="proctoring-modal-icon">
                  <i className="fa-solid fa-shield-halved" />
                </div>
                <div>
                  <h2>Proctoring Log</h2>
                  <p className="proctoring-modal-subtitle">
                    {studentInfo.name}
                  </p>
                </div>
              </div>
              <button
                className="proctoring-modal-close"
                onClick={() => {
                  setShowProctoringModal(false);
                  setProctoringTab("violations");
                  setEnlargedSnapshotIndex(null);
                }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="proctoring-modal-tabs">
              <button
                className={`proctoring-modal-tab ${proctoringTab === "violations" ? "active" : ""}`}
                onClick={() => setProctoringTab("violations")}
              >
                Violations ({proctoringLog.violations?.length || 0})
              </button>
              <button
                className={`proctoring-modal-tab ${proctoringTab === "snapshots" ? "active" : ""}`}
                onClick={() => setProctoringTab("snapshots")}
              >
                Snapshots ({proctoringLog.snapshots?.length || 0})
              </button>
            </div>

            <div className="proctoring-modal-body">
              {proctoringTab === "violations" && (
                <>
                  {!proctoringLog.violations ||
                  proctoringLog.violations.length === 0 ? (
                    <p className="proctoring-modal-empty">
                      No violations recorded.
                    </p>
                  ) : (
                    <div className="proctoring-timeline">
                      {proctoringLog.violations.map((v, idx) => {
                        const date = new Date(v.timestamp);
                        const timeStr = date.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: true,
                        });
                        const dateStr = date.toLocaleDateString([], {
                          day: "numeric",
                          month: "short",
                        });

                        // Map type to icon and label
                        const typeMap = {
                          tab_switch: {
                            icon: "fa-window-restore",
                            label: "Tab Switch",
                            color: "#f59e0b",
                          },
                          window_lost_focus: {
                            icon: "fa-eye-slash",
                            label: "Window Lost Focus",
                            color: "#ef4444",
                          },
                          window_blur: {
                            icon: "fa-eye-slash",
                            label: "Window Blur",
                            color: "#ef4444",
                          },
                          devtools: {
                            icon: "fa-code",
                            label: "Developer Tools",
                            color: "#8b5cf6",
                          },
                        };
                        const mapped = typeMap[v.type] || {
                          icon: "fa-circle-exclamation",
                          label: v.type || "Unknown",
                          color: "#64748b",
                        };

                        return (
                          <div className="proctoring-timeline-item" key={idx}>
                            <div className="proctoring-timeline-num">
                              #{idx + 1}
                            </div>
                            <div
                              className="proctoring-timeline-dot"
                              style={{ background: mapped.color }}
                            >
                              <i className={`fa-solid ${mapped.icon}`} />
                            </div>
                            <div className="proctoring-timeline-content">
                              <span className="proctoring-timeline-label">
                                {mapped.label}
                              </span>
                              <span className="proctoring-timeline-time">
                                <i className="fa-regular fa-clock" /> {dateStr}{" "}
                                · {timeStr}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {proctoringTab === "snapshots" && (
                <>
                  {!proctoringLog.snapshots ||
                  proctoringLog.snapshots.length === 0 ? (
                    <p className="proctoring-modal-empty">
                      No video snapshots captured.
                    </p>
                  ) : (
                    <div className="proctoring-snapshots-grid">
                      {proctoringLog.snapshots.map((snap, idx) => {
                        const date = new Date(snap.capturedAt);
                        const timeStr = date.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: true,
                        });

                        return (
                          <div
                            key={idx}
                            className="proctoring-snapshot-card"
                            onClick={() => setEnlargedSnapshotIndex(idx)}
                          >
                            <img
                              src={snap.url}
                              alt={`Snapshot ${idx + 1}`}
                              loading="lazy"
                            />
                            <div className="proctoring-snapshot-time">
                              <i className="fa-regular fa-clock" /> {timeStr}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Snapshot Enlarge Overlay */}
      {enlargedSnapshotIndex !== null &&
        proctoringLog?.snapshots?.[enlargedSnapshotIndex] && (
          <div
            className="proctoring-snapshot-overlay"
            onClick={() => setEnlargedSnapshotIndex(null)}
          >
            <div
              className="proctoring-snapshot-large-container"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="proctoring-snapshot-nav">
                <button
                  className="proctoring-nav-btn"
                  onClick={() =>
                    setEnlargedSnapshotIndex((prev) => Math.max(0, prev - 1))
                  }
                  disabled={enlargedSnapshotIndex === 0}
                >
                  <i className="fa-solid fa-chevron-left" />
                </button>
              </div>

              <div className="proctoring-snapshot-content">
                <img
                  src={proctoringLog.snapshots[enlargedSnapshotIndex].url}
                  alt="Enlarged snapshot"
                  className="proctoring-snapshot-large"
                />
                <div className="proctoring-snapshot-large-info">
                  <span>
                    <i className="fa-regular fa-clock" />{" "}
                    {new Date(
                      proctoringLog.snapshots[enlargedSnapshotIndex].capturedAt,
                    ).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true,
                    })}
                    <span className="proctoring-snapshot-counter">
                      ({enlargedSnapshotIndex + 1} of{" "}
                      {proctoringLog.snapshots.length})
                    </span>
                  </span>
                  <button
                    className="proctoring-close-btn"
                    onClick={() => setEnlargedSnapshotIndex(null)}
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
              </div>

              <div className="proctoring-snapshot-nav">
                <button
                  className="proctoring-nav-btn"
                  onClick={() =>
                    setEnlargedSnapshotIndex((prev) =>
                      Math.min(proctoringLog.snapshots.length - 1, prev + 1),
                    )
                  }
                  disabled={
                    enlargedSnapshotIndex === proctoringLog.snapshots.length - 1
                  }
                >
                  <i className="fa-solid fa-chevron-right" />
                </button>
              </div>
            </div>
          </div>
        )}

      {/* ── Image Lightbox ─────────────────────────────────────────── */}
      {lightboxUrl && (
        <div className="exam-lightbox" onClick={() => setLightboxUrl(null)}>
          <button
            className="exam-lightbox-close"
            onClick={() => setLightboxUrl(null)}
            type="button"
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <img
            src={lightboxUrl}
            alt="Diagram enlarged view"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
            style={{
              backgroundColor: "#fff",
              height: "100dvh",
              width: "100vw",
              objectFit: "contain",
            }}
          />
        </div>
      )}
    </div>
  );
}
